import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaWineGlassAlt, FaSmoking, FaCheck } from "react-icons/fa";
import {
  FiKey,
  FiUser,
  FiBell,
  FiFileText,
  FiHeart,
  FiSliders,
  FiLock,
  FiClock,
  FiLogOut,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
  FiEdit,
  FiMail,
  FiPhone,
  FiEye,
  FiEyeOff,
  FiCalendar, 
  FiAlertTriangle, 
  FiMapPin,
  FiX,
  FiCheck,
  FiShield,
  FiDatabase,
  FiInfo,
  FiUserX,
  FiXCircle,
  FiSend,
  FiRefreshCw
} from "react-icons/fi";
import { commonPasswords } from "../../services/passwordUtils";
import "./usercss/UserProfile.css";
import "../registration/registrationcss/Register.css";
import "../staffpages/staffcss/sharedprofile.css";
import { useLocation } from "react-router-dom";
import Footer from "../../components/AppFooter";
import { db } from "../../firebase";
import { auth } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
  serverTimestamp,
  deleteField,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from "firebase/auth";
import { userService, psgcService, notificationService } from "../../services/firestoreService";
import { appointmentService } from "../../services/appointmentService";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";
import UserChangePassword from "../../components/usercomponents/UserChangePassword";
import TwoFactorOtpModal from "../../components/otpmodals/TwoFactorOtpModal";
import UserChangeOtpModal from "../../components/otpmodals/UserChangeOtpModal";

import alcoholIcon from "../../images/alcohol-icon 1.png";
import cigaretteIcon from "../../images/cigarrete-icon 1.png";
import { sessionService } from "../../services/sessionService";
import { apiRevokeSession, apiLogoutAllSessions } from "../../services/sessionApiService";

// Safe localStorage user parse
function getStoredUser() {
  try {
    const s = localStorage.getItem("user");
    return s ? JSON.parse(s) : null;
  } catch (err) {
    console.warn("Invalid stored user", err);
    return null;
  }
}

const normalizePhoneDigits = (value) => String(value || "").replace(/\D/g, "");
const getLast10Digits = (value) => normalizePhoneDigits(value).slice(-10);

const RELATIONSHIP_OPTIONS = [
  "Parent",
  "Partner",
  "Guardian",
  "Sibling",
  "Relative",
];

// Firestore optimization: Global shared cache reference for user data across all sub-panels
const sharedUserDataCacheRef = { current: null };

// Shared refs for batching across components
const sharedBatchingRefs = {
  pendingUpdates: {},
  saveTimeout: null,
  isSaving: false
};

// Module-level helper function to build update structure
const buildUserUpdateModule = (currentUser, updates) => {
  if (!userService.isGroupedStructure(currentUser)) {
    return { ...updates };
  }

  // Build grouped structure updates using dot notation
  const groupedUpdate = {};
  
  // Personal information updates (excluding firstName/lastName from Firestore)
  // firstName and lastName are not stored in Firestore but kept for UIC generation
  if (updates.sex !== undefined) {
    groupedUpdate.sex = updates.sex;
    groupedUpdate["personalInformation.sex"] = updates.sex;
  }
  if (updates.dob !== undefined) {
    groupedUpdate.dob = updates.dob;
    groupedUpdate["personalInformation.dob"] = updates.dob;
  }
  
  // Contact details updates (keep both root and nested for compatibility)
  if (updates.region !== undefined) {
    groupedUpdate.region = updates.region;
    groupedUpdate["contactDetails.region"] = updates.region;
  }
  if (updates.province !== undefined) {
    groupedUpdate.province = updates.province;
    groupedUpdate["contactDetails.province"] = updates.province;
  }
  if (updates.city !== undefined) {
    groupedUpdate.city = updates.city;
    groupedUpdate["contactDetails.city"] = updates.city;
  }
  if (updates.barangay !== undefined) {
    groupedUpdate.barangay = updates.barangay;
    groupedUpdate["contactDetails.barangay"] = updates.barangay;
  }
  if (updates.street !== undefined) {
    groupedUpdate.street = updates.street;
    groupedUpdate["contactDetails.street"] = updates.street;
  }
  if (updates.contact !== undefined) {
    groupedUpdate.contact = updates.contact;
    groupedUpdate["contactDetails.contact"] = updates.contact;
  }
  if (updates.email !== undefined) {
    groupedUpdate.email = updates.email;
    groupedUpdate["contactDetails.email"] = updates.email;
  }

  // Auth and identification fields at root level
  if (updates.emailLower !== undefined) groupedUpdate.emailLower = updates.emailLower;
  if (updates.username !== undefined) groupedUpdate.username = updates.username;
  if (updates.usernameLower !== undefined) groupedUpdate.usernameLower = updates.usernameLower;
  if (updates.phone !== undefined) groupedUpdate.phone = updates.phone;
  if (updates.phoneLast10 !== undefined) groupedUpdate.phoneLast10 = updates.phoneLast10;
  if (updates.password !== undefined) {
    groupedUpdate.password = updates.password;
    groupedUpdate["accountSetup.password"] = updates.password;
  }

  // Guardian contact details (for minors) - only nested structure
  if (updates.guardianFullName !== undefined) {
    groupedUpdate["guardianContactDetails.guardianFullName"] = updates.guardianFullName;
  }
  if (updates.guardianRelationshipToUser !== undefined) {
    groupedUpdate["guardianContactDetails.guardianRelationshipToUser"] = updates.guardianRelationshipToUser;
  }
  if (updates.guardianContactNumber !== undefined) {
    groupedUpdate["guardianContactDetails.guardianContactNumber"] = updates.guardianContactNumber;
  }
  if (updates.guardianAddress !== undefined) {
    groupedUpdate["guardianContactDetails.guardianAddress"] = updates.guardianAddress;
  }

  // Accompanist contact details (for non-minors) - only nested structure
  if (updates.accompanistFullName !== undefined) {
    groupedUpdate["accompanistContactDetails.accompanistFullName"] = updates.accompanistFullName;
  }
  if (updates.accompanistRelationshipToUser !== undefined) {
    groupedUpdate["accompanistContactDetails.accompanistRelationshipToUser"] = updates.accompanistRelationshipToUser;
  }
  if (updates.accompanistContactNumber !== undefined) {
    groupedUpdate["accompanistContactDetails.accompanistContactNumber"] = updates.accompanistContactNumber;
  }
  if (updates.accompanistAddress !== undefined) {
    groupedUpdate["accompanistContactDetails.accompanistAddress"] = updates.accompanistAddress;
  }

  // Health information fields - keep at root level for now
  if (updates.medicalHistory !== undefined) groupedUpdate.medicalHistory = updates.medicalHistory;
  if (updates.medicalOther !== undefined) groupedUpdate.medicalOther = updates.medicalOther;
  if (updates.allergies !== undefined) groupedUpdate.allergies = updates.allergies;
  if (updates.allergyOther !== undefined) groupedUpdate.allergyOther = updates.allergyOther;
  if (updates.alcoholUse !== undefined) groupedUpdate.alcoholUse = updates.alcoholUse;
  if (updates.smoking !== undefined) groupedUpdate.smoking = updates.smoking;
  if (updates.hormonalMedications !== undefined) groupedUpdate.hormonalMedications = updates.hormonalMedications;
  if (updates.stiHistory !== undefined) groupedUpdate.stiHistory = updates.stiHistory;
  if (updates.stiOther !== undefined) groupedUpdate.stiOther = updates.stiOther;
  if (updates.hivStatus !== undefined) groupedUpdate.hivStatus = updates.hivStatus;
  if (updates.hivOther !== undefined) groupedUpdate.hivOther = updates.hivOther;
  if (updates.lastTestedDate !== undefined) groupedUpdate.lastTestedDate = updates.lastTestedDate;
  if (updates.additionalHealthHistory !== undefined) groupedUpdate.additionalHealthHistory = updates.additionalHealthHistory;
  if (updates.additionalOther !== undefined) groupedUpdate.additionalOther = updates.additionalOther;
  if (updates.contraceptionOther !== undefined) groupedUpdate.contraceptionOther = updates.contraceptionOther;
  if (updates.pastSurgeries !== undefined) groupedUpdate.pastSurgeries = updates.pastSurgeries;
  if (updates.surgeryOther !== undefined) groupedUpdate.surgeryOther = updates.surgeryOther;
  if (updates.currentMedications !== undefined) groupedUpdate.currentMedications = updates.currentMedications;

  return groupedUpdate;
};

// Module-level batched save operation with debouncing
const batchUpdateUser = async (updates, loggedInUser) => {
  if (sharedBatchingRefs.isSaving) {
    // Queue the update if another save is in progress
    Object.assign(sharedBatchingRefs.pendingUpdates, updates);
    return;
  }

  // Merge with any pending updates
  Object.assign(sharedBatchingRefs.pendingUpdates, updates);

  // Clear any existing timeout
  if (sharedBatchingRefs.saveTimeout) {
    clearTimeout(sharedBatchingRefs.saveTimeout);
  }

  // Debounce for 500ms to allow rapid updates to be batched
  sharedBatchingRefs.saveTimeout = setTimeout(async () => {
    sharedBatchingRefs.isSaving = true;

    try {
      const finalUpdates = { ...sharedBatchingRefs.pendingUpdates };
      sharedBatchingRefs.pendingUpdates = {};

      if (Object.keys(finalUpdates).length === 0) {
        return null;
      }

      const structuredUpdate = buildUserUpdateModule(loggedInUser, finalUpdates);

      await updateDoc(doc(db, "users", loggedInUser.uid), {
        ...structuredUpdate,
        updatedAt: serverTimestamp(),
      });

      // Merge updates into user object for local storage
      const mergedUser = { ...loggedInUser };
      Object.keys(structuredUpdate).forEach(key => {
        const parts = key.split('.');
        if (parts.length > 1) {
          let current = mergedUser;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) current[parts[i]] = {};
            current = current[parts[i]];
          }
          current[parts[parts.length - 1]] = structuredUpdate[key];
        } else {
          mergedUser[key] = structuredUpdate[key];
        }
      });

      localStorage.setItem("user", JSON.stringify(mergedUser));

      // Update cache
      sharedUserDataCacheRef.current = mergedUser;

      return mergedUser;
    } catch (error) {
      console.error('Batch update failed:', error);
      throw error;
    } finally {
      sharedBatchingRefs.isSaving = false;

      // Process any pending updates that arrived during the save
      if (Object.keys(sharedBatchingRefs.pendingUpdates).length > 0) {
        await batchUpdateUser({}, loggedInUser);
      }
    }
  }, 500);
};

// Module-level immediate save (for critical updates that shouldn't be debounced)
const immediateUpdateUser = async (updates, loggedInUser) => {
  // Clear any pending batched updates
  if (sharedBatchingRefs.saveTimeout) {
    clearTimeout(sharedBatchingRefs.saveTimeout);
    sharedBatchingRefs.saveTimeout = null;
  }

  // Merge with any pending updates
  Object.assign(sharedBatchingRefs.pendingUpdates, updates);
  const finalUpdates = { ...sharedBatchingRefs.pendingUpdates };
  sharedBatchingRefs.pendingUpdates = {};

  if (Object.keys(finalUpdates).length === 0) {
    return null;
  }

  const structuredUpdate = buildUserUpdateModule(loggedInUser, finalUpdates);

  await updateDoc(doc(db, "users", loggedInUser.uid), {
    ...structuredUpdate,
    updatedAt: serverTimestamp(),
  });

  // Merge updates into user object for local storage
  const mergedUser = { ...loggedInUser };
  Object.keys(structuredUpdate).forEach(key => {
    const parts = key.split('.');
    if (parts.length > 1) {
      let current = mergedUser;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = structuredUpdate[key];
    } else {
      mergedUser[key] = structuredUpdate[key];
    }
  });

  localStorage.setItem("user", JSON.stringify(mergedUser));

  // Update cache
  sharedUserDataCacheRef.current = mergedUser;

  return mergedUser;
};

// Cache warming function to ensure shared cache is populated
const warmUserCache = async (user) => {
  if (!user?.uid) return null;

  // Check if cache is already populated
  if (sharedUserDataCacheRef.current && sharedUserDataCacheRef.current.uid === user.uid) {
    return sharedUserDataCacheRef.current;
  }

  try {
    const userData = await userService.getUser(user.uid);
    sharedUserDataCacheRef.current = userData;

    return userData;
  } catch (error) {
    console.error('Failed to warm user cache:', error);
    return null;
  }
};
function AccountPanel({ navigate, showBanner, twoFactorEnabled, setTwoFactorEnabled, updateTwoFactor }) {
  const { language } = useLanguage();
  const t = translations[language];
  const location = useLocation();

  const CONDITIONS = [
    "Obesity",
    "Liver Disease",
    "Diabetes",
    "Hypertension",
    "Chronic Kidney Disease",
    "Heart Disease",
    "Others (Please Specify)",
  ];

  const ALLERGIES = [
    "Penicillin / Antibiotics",
    "Hormonal Medications (Pills, Patches, Injections)",
    "Others (Please Specify)",
  ];

  const MEDICATION_HISTORY = [
    "IUD", "Implant", "Injection", "Pills",
    "Others (Please Specify)",
  ];

  const INFECTIONS_OPTIONS = [
    "Chlamydia",
    "Gonorrhea",
    "Syphilis",
    "HPV",
    "Hepatitis B",
    "Hepatitis C",
    "Genital Herpes",
    "Trichomoniasis",
    "Others (Please Specify)",
  ];

  const HIV_OPTIONS = [
    "Positive",
    "Negative",
    "Unknown",
  ];

  const CONSULTATION_REASONS = [
    "Family Planning / Contraceptive Guidance",
    "HIV Screening",
  ];

  const [personalEdit, setPersonalEdit] = useState(false);
  const [accountEdit, setAccountEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("personal");
  const [healthRecordsCount, setHealthRecordsCount] = useState(0);
  const [confirmedAppointmentsCount, setConfirmedAppointmentsCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [createdAt, setCreatedAt] = useState(null);
  const [lastLogin, setLastLogin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  
  // User Change OTP Modal State
  const [showUserChangeOtpModal, setShowUserChangeOtpModal] = useState(false);
  const [userChangeType, setUserChangeType] = useState(null);
  const [userChangeData, setUserChangeData] = useState(null);
  const [passwordChangeData, setPasswordChangeData] = useState(null);
  
  // -------------------- HEALTH & LIFESTYLE STATE --------------------
  const [healthEditing, setHealthEditing] = useState(false);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [medicalOther, setMedicalOther] = useState("");
  const [allergies, setAllergies] = useState([]);
  const [allergyOther, setAllergyOther] = useState("");
  const [alcoholUse, setAlcoholUse] = useState("");
  const [smoking, setSmoking] = useState("");
  const [hormonalMedicationText, setHormonalMedicationText] = useState("");
  
  // Original values for Health & Lifestyle (for cancel)
  const [origMedicalHistory, setOrigMedicalHistory] = useState([]);
  const [origMedicalOther, setOrigMedicalOther] = useState("");
  const [origAllergies, setOrigAllergies] = useState([]);
  const [origAllergyOther, setOrigAllergyOther] = useState("");
  const [origAlcoholUse, setOrigAlcoholUse] = useState("");
  const [origSmoking, setOrigSmoking] = useState("");
  const [origHormonalMedicationText, setOrigHormonalMedicationText] = useState("");

  // HIV Status / Sexual Health History
  const [hivEditing, setHivEditing] = useState(false);
  const [hivSelection, setHivSelection] = useState([]); // checkbox options
  const [stiOther, setStiOther] = useState("");
  const [hivLeftBox, setHivLeftBox] = useState(""); // custom "radio" buttons
  const [hivRightBoxText, setHivRightBoxText] = useState("");
  const [lastTestedDate, setLastTestedDate] = useState(""); // Added date state
  
  // Original values for HIV Status (for cancel)
  const [origHivSelection, setOrigHivSelection] = useState([]);
  const [origStiOther, setOrigStiOther] = useState("");
  const [origHivLeftBox, setOrigHivLeftBox] = useState("");
  const [origHivRightBoxText, setOrigHivRightBoxText] = useState("");
  const [origLastTestedDate, setOrigLastTestedDate] = useState("");

  // Additional Health Info (Medication History)
  const [additionalEditing, setAdditionalEditing] = useState(false);
  const [additionalHistory, setAdditionalHistory] = useState([]);
  const [additionalOther, setAdditionalOther] = useState("");
  const [contraceptionOtherText, setContraceptionOtherText] = useState("");
  const [leftBox, setLeftBox] = useState([]);
  const [leftBoxOther, setLeftBoxOther] = useState("");
  const [rightBoxText, setRightBoxText] = useState("");
  
  // Original values for Additional Health Info (for cancel)
  const [origAdditionalHistory, setOrigAdditionalHistory] = useState([]);
  const [origAdditionalOther, setOrigAdditionalOther] = useState("");
  const [origContraceptionOtherText, setOrigContraceptionOtherText] = useState("");
  const [origLeftBox, setOrigLeftBox] = useState([]);
  const [origLeftBoxOther, setOrigLeftBoxOther] = useState("");
  const [origRightBoxText, setOrigRightBoxText] = useState("");

  // Validation states for "Other" fields
  const [healthFieldErrors, setHealthFieldErrors] = useState({});
  const [stiFieldErrors, setStiFieldErrors] = useState({});
  const [additionalFieldErrors, setAdditionalFieldErrors] = useState({});

  // Clinical Services
  const [clinicalEditing, setClinicalEditing] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyRelationship, setEmergencyRelationship] = useState("");
  const [emergencyRelationshipOther, setEmergencyRelationshipOther] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [userSex, setUserSex] = useState("");
  const [isAssistedAccount, setIsAssistedAccount] = useState(false);

  // Original values for Clinical Services (for cancel)
  const [origSelectedReasons, setOrigSelectedReasons] = useState([]);
  const [origEmergencyName, setOrigEmergencyName] = useState("");
  const [origEmergencyRelationship, setOrigEmergencyRelationship] = useState("");
  const [origEmergencyRelationshipOther, setOrigEmergencyRelationshipOther] = useState("");
  const [origEmergencyContact, setOrigEmergencyContact] = useState("");
  
  // For "Other" option in relationship dropdown
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  
  const STI_ALIASES = {
      chlamydia: "Chlamydia",
      chylamydia: "Chlamydia",
      "other": "Others (Please Specify)",
      "other (please specify)": "Others (Please Specify)",
      "others": "Others (Please Specify)",
      "others (please specify)": "Others (Please Specify)",
      "Other": "Others (Please Specify)",
      "Others (please specify)": "Others (Please Specify)"
    };

  const SURGERY_ALIASES = {
    "ceasrean": "Cesarean Section",
    "ceasrean section": "Cesarean Section",
    "cesarean": "Cesarean Section",
    "cesarean section": "Cesarean Section",
    "other": "Others (Please Specify)",
    "other (please specify)": "Others (Please Specify)",
    "others": "Others (Please Specify)",
    "others (please specify)": "Others (Please Specify)",
    "Other": "Others (Please Specify)",
    "Others (please specify)": "Others (Please Specify)"
  };

  const OTHER_ALIAS = {
    "other": "Others (Please Specify)",
    "other (please specify)": "Others (Please Specify)",
    "other (Please specify)": "Others (Please Specify)",
    "others": "Others (Please Specify)",
    "others (please specify)": "Others (Please Specify)",
    "others (Please specify)": "Others (Please Specify)",
    "Others": "Others (Please Specify)",
    "Others (please specify)": "Others (Please Specify)",
    "Others (Please specify)": "Others (Please Specify)"
  };

  // ---- Session management state ----
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokeLoadingId, setRevokeLoadingId] = useState(null);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  const [showLogoutAllConfirm, setShowLogoutAllConfirm] = useState(false);
  const [sessionRevoked, setSessionRevoked] = useState(false);
  const currentSessionId = sessionService.getCurrentSessionId();

  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [selectedProvinceId, setSelectedProvinceId] = useState("");
  const [selectedCityId, setSelectedCityId] = useState("");
  const [selectedBarangayId, setSelectedBarangayId] = useState("");
  const [isRegionsLoading, setIsRegionsLoading] = useState(false);
  const [isProvincesLoading, setIsProvincesLoading] = useState(false);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [isBarangaysLoading, setIsBarangaysLoading] = useState(false);

  // Assisted Account State
  const [assistedEdit, setAssistedEdit] = useState(false);
  const [isAssistedOtherTyping, setIsAssistedOtherTyping] = useState(false);
  const [tempAssistedData, setTempAssistedData] = useState({
    fullName: "",
    relationship: "",
    contact: "",
    address: "",
  });
  const [originalAssistedData, setOriginalAssistedData] = useState({
    fullName: "",
    relationship: "",
    contact: "",
    address: "",
  });

  // Firestore optimization: Cache references to prevent duplicate reads
  const cachedAppointmentsRef = useRef(null);
  const sessionRecoveredRef = useRef(false);
  const notificationUserCacheRef = useRef(null);

  // Firestore optimization: Location data caching
  const locationCacheRef = useRef({
    regions: null,
    provinces: null,
    cities: null,
    barangays: null
  });

  // Validation cache to prevent duplicate Firestore queries
  const validationCacheRef = useRef({
    username: new Map(),
    email: new Map(),
    contact: new Map()
  });

  // Debounce timeout refs
  const debounceTimeoutsRef = useRef({
    username: null,
    email: null,
    contact: null
  });



  const [personalData, setPersonalData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    contact: "",
    email: "",
    dob: "",
    sex: "",
    address: "",
    region: "",
    province: "",
    city: "",
    barangay: "",
  });
  const [tempPersonalData, setTempPersonalData] = useState({ ...personalData });
  const [originalPersonalData, setOriginalPersonalData] = useState({ ...personalData }); // Track original values

  const fetchJson = async (url, signal) => {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`PSGC API error: ${response.status}`);
    }
    return response.json();
  };

  const normalizeList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  // Validation cache helper with TTL (5 minutes)
  const getCachedValidation = (field, value) => {
    const cache = validationCacheRef.current[field];
    if (!cache) return null;
    
    const cached = cache.get(value);
    if (!cached) return null;
    
    // Check if cache is still valid (5 minutes TTL)
    if (Date.now() - cached.timestamp > 5 * 60 * 1000) {
      cache.delete(value);
      return null;
    }
    
    return cached.result;
  };

  const setCachedValidation = (field, value, result) => {
    const cache = validationCacheRef.current[field];
    if (!cache) return;
    
    cache.set(value, {
      result,
      timestamp: Date.now()
    });
  };

  // Debounce helper
  const debounceValidation = (field, delay) => {
    return new Promise((resolve) => {
      if (debounceTimeoutsRef.current[field]) {
        clearTimeout(debounceTimeoutsRef.current[field]);
      }
      
      debounceTimeoutsRef.current[field] = setTimeout(() => {
        resolve();
      }, delay);
    });
  };

  // Clear validation cache for specific field
  const clearValidationCache = (field, value) => {
    const cache = validationCacheRef.current[field];
    if (cache && value) {
      cache.delete(value);
    }
  };

  // Clear all validation cache
  const clearAllValidationCache = () => {
    Object.values(validationCacheRef.current).forEach(cache => {
      if (cache) cache.clear();
    });
  };

  // Custom Scrollable Dropdown Component
  function ScrollableDropdown({
    label,
    options,
    value,
    onChange,
    placeholder,
    disabled,
    loading,
    id
  }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
      function handleClickOutside(event) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => opt.psgc_id === value);

    return (
      <div className="input-group select-wrapper custom-dropdown" ref={dropdownRef}>
        <label htmlFor={id}>{label}</label>
        <button
          type="button"
          className={`dropdown-button ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span className={selectedOption ? '' : 'placeholder'}>
            {selectedOption ? selectedOption.name : placeholder}
          </span>
          <FiChevronDown className={`dropdown-arrow ${isOpen ? 'open' : ''}`} />
        </button>
        {isOpen && (
          <div className="dropdown-menu">
            {loading ? (
              <div className="dropdown-loading">Loading...</div>
            ) : options.length === 0 ? (
              <div className="dropdown-empty">No options available</div>
            ) : (
              <div className="dropdown-options">
                {options.map((option) => (
                  <div
                    key={option.psgc_id}
                    className={`dropdown-item ${value === option.psgc_id ? 'selected' : ''}`}
                    onClick={() => {
                      onChange(option.psgc_id);
                      setIsOpen(false);
                    }}
                  >
                    {option.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const findByName = (list, name) =>
    list.find((item) => item.name.toLowerCase() === String(name).toLowerCase());

  const findById = (list, id) =>
    list.find((item) => String(item.psgc_id) === String(id));



  // Helper function to parse Firebase timestamps
  const parseFirebaseTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    // If it's a Firestore Timestamp object with toDate method
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // If it's a number (Unix timestamp in milliseconds or seconds)
    if (typeof timestamp === 'number') {
      // Firebase sometimes uses seconds (10 digits) instead of milliseconds (13 digits)
      if (timestamp < 10000000000) {
        return new Date(timestamp * 1000);
      }
      return new Date(timestamp);
    }
    
    // If it's a string, try to parse it
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    return null;
  };

  /**
   * Format a Date into a human-readable relative string for the session list.
   * e.g. "Just now", "5 minutes ago", "2 hours ago", "3 days ago", or a date string.
   */
  const formatSessionTime = (date) => {
    if (!date) return "Unknown";
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  const processUserData = (finalUser) => {
    // Check if data is grouped or flat structure
    let userData;
    if (userService.isGroupedStructure(finalUser)) {
      // Extract data from grouped structure
      userData = userService.extractUserData(finalUser);
    } else {
      // Use flat structure directly
      userData = finalUser;
    }

    // Construct full address from location components
    const addressParts = [
      userData.barangay,
      userData.city,
      userData.province,
      userData.region
    ].filter(Boolean);
    const fullAddress = addressParts.join(", ");

    // Get firstName and lastName from personalInformation nested group or localStorage
    const firstName = userData.personalInformation?.firstName || 
                      userData.firstName || 
                      (JSON.parse(localStorage.getItem('registrationDraft') || '{}').firstName || '');
    const lastName = userData.personalInformation?.lastName || 
                     userData.lastName || 
                     (JSON.parse(localStorage.getItem('registrationDraft') || '{}').lastName || '');

    const fullName = [
      firstName,
      lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim() || userData.uniqueIdentifierCode || "User";

    const processedUserData = {
      fullName,
      firstName,
      lastName,
      username: userData.username || "",
      email: userData.email || "",
      contact: getLast10Digits(userData.phone || userData.contact || ""),
      dob: userData.dob || "",
      sex: userData.sex ? (userData.sex.charAt(0).toUpperCase() + userData.sex.slice(1)) : "",
      address: fullAddress,
      region: userData.region || "",
      province: userData.province || "",
      city: userData.city || "",
      barangay: userData.barangay || "",
      uniqueIdentifierCode: userData.uniqueIdentifierCode || "",
      
      // Assisted Account Data
      assistanceRequired: userData.assistanceRequired || "no",
      reasonForAssistance: userData.reasonForAssistance || [],
      disabilityText: userData.disabilityText || "",
      reasonText: userData.reasonText || "",
      
      // Guardian/Accompanist Data - display based on account type
      guardianFullName: "",
      guardianRelationship: "",
      guardianContact: "",
      guardianAddress: "",
    };

    // Set appropriate contact details based on account type
    const reasonForAssistance = userData.reasonForAssistance || [];
    const isMinor = Array.isArray(reasonForAssistance)
      ? reasonForAssistance.includes("minor")
      : reasonForAssistance === "minor";

    if (isMinor) {
      // For minors, display guardian details - check both nested and root level
      processedUserData.guardianFullName = userData.guardianContactDetails?.guardianFullName || userData.guardianFullName || "";
      processedUserData.guardianRelationship = userData.guardianContactDetails?.guardianRelationshipToUser || userData.guardianRelationshipToUser || "";
      processedUserData.guardianContact = getLast10Digits(userData.guardianContactDetails?.guardianContactNumber || userData.guardianContactNumber || "");
      processedUserData.guardianAddress = userData.guardianContactDetails?.guardianAddress || userData.guardianAddress || "";
    } else {
      // For non-minors, display accompanist details - check both nested and root level
      processedUserData.guardianFullName = userData.accompanistContactDetails?.accompanistFullName || userData.accompanistFullName || "";
      processedUserData.guardianRelationship = userData.accompanistContactDetails?.accompanistRelationshipToUser || userData.accompanistRelationshipToUser || "";
      processedUserData.guardianContact = getLast10Digits(userData.accompanistContactDetails?.accompanistContactNumber || userData.accompanistContactNumber || "");
      processedUserData.guardianAddress = userData.accompanistContactDetails?.accompanistAddress || userData.accompanistAddress || "";
    }

    return processedUserData;
  };

  // Load user data and construct display fields
  const loadUserData = async () => {
    const userFromState = location.state?.user;
    const userFromStorage = getStoredUser();
    let finalUser = userFromState || userFromStorage;

    if (!finalUser) {
      alert('No user data found. Please log in again.');
      return;
    }

    // Firestore optimization: Check cache first to prevent duplicate reads
    if (sharedUserDataCacheRef.current && sharedUserDataCacheRef.current.uid === finalUser.uid) {
      finalUser = { ...sharedUserDataCacheRef.current, ...finalUser };
    }

    try {
      let firestoreUser = null;

      // Use the most efficient lookup: UID is the primary key with retry logic
      if (finalUser.uid) {
        const maxRetries = 2;
        let retryCount = 0;
        
        while (retryCount <= maxRetries && !firestoreUser) {
          try {
            firestoreUser = await userService.getUser(finalUser.uid);
            if (firestoreUser) break;
            
            // Exponential backoff before retry
            if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
            }
          } catch (retryError) {
            if (retryCount === maxRetries) {
              throw retryError;
            }
          }
          retryCount++;
        }
      }

      // Fallback to email lookup only if UID lookup failed after retries
      if (!firestoreUser && finalUser.email) {
        // Circuit breaker: Skip email lookup if it has failed recently
        const emailLookupFailureKey = `email_lookup_failure_${finalUser.email}`;
        const lastFailureTime = localStorage.getItem(emailLookupFailureKey);
        const circuitBreakerTimeout = 5 * 60 * 1000; // 5 minutes
        
        if (lastFailureTime && Date.now() - parseInt(lastFailureTime) < circuitBreakerTimeout) {
          // Circuit breaker active
        } else {
          try {
            firestoreUser = await userService.getUserByEmail(finalUser.email);
            // Clear circuit breaker on success
            localStorage.removeItem(emailLookupFailureKey);
          } catch (emailError) {
            // Set circuit breaker
            localStorage.setItem(emailLookupFailureKey, Date.now().toString());
          }
        }
      }

      if (firestoreUser) {
        // Preserve fresh email/contact from location state/storage if newer than Firestore
        const freshFieldsFromState = { ...finalUser };
        finalUser = { ...firestoreUser, ...freshFieldsFromState };

        // Only use Firestore values for critical fields if state doesn't have them
        const criticalFields = ['email', 'contact', 'phone', 'phoneLast10', 'emailLower', 'createdAt', 'lastLogin'];
        criticalFields.forEach(field => {
          if (!finalUser[field] && firestoreUser[field]) {
            finalUser[field] = firestoreUser[field];
          }
        });

        // Always use Firestore emergencyContact if it exists (it's the source of truth)
        if (firestoreUser.emergencyContact && Object.keys(firestoreUser.emergencyContact).length > 0) {
          finalUser.emergencyContact = firestoreUser.emergencyContact;
        }

        // Cache the user data to prevent duplicate reads
        sharedUserDataCacheRef.current = finalUser;
        
        // Firestore optimization: Ensure cache is warmed for settings panels
        // This prevents conditional fetches in ProfileNotifications and ProfileContent
        warmUserCache(finalUser);
      }
    } catch (error) {
      // Silent error handling for user profile fetch
    }

    const userData = processUserData(finalUser);

    setPersonalData(userData);
    setTempPersonalData(userData);
    setOriginalPersonalData(userData);

    // Set Assisted Account Data - use raw finalUser to check both nested and root level
    // Strip +63 prefix from contact number for form display
    const rawContact = finalUser.guardianContactDetails?.guardianContactNumber || finalUser.guardianContactNumber || "";
    const contactDigits = rawContact.replace(/\D/g, "").slice(-10);

    const assisted = {
      fullName: finalUser.guardianContactDetails?.guardianFullName || finalUser.guardianFullName || "",
      relationship: finalUser.guardianContactDetails?.guardianRelationshipToUser || finalUser.guardianRelationshipToUser || "",
      contact: contactDigits,
      address: finalUser.guardianContactDetails?.guardianAddress || finalUser.guardianAddress || "",
    };

    setTempAssistedData(assisted);
    setOriginalAssistedData(assisted);
    
    // -------------------- Health & Lifestyle --------------------
    setMedicalHistory(normalizeSelectionList(finalUser.medicalHistory, OTHER_ALIAS));
    setMedicalOther(finalUser.medicalOther || "");
    setAllergies(normalizeSelectionList(finalUser.allergies, OTHER_ALIAS));
    setAllergyOther(finalUser.allergyOther || "");
    setAlcoholUse(finalUser.alcoholUse || "");
    setSmoking(finalUser.smoking || "");
    setHormonalMedicationText(finalUser.hormonalMedications || "");
    
    // -------------------- Sexual Health History --------------------
    setHivSelection(normalizeSelectionList(finalUser.stiHistory, STI_ALIASES));
    setStiOther(finalUser.stiOther || "");
    setHivLeftBox(finalUser.hivStatus || "");
    setHivRightBoxText(finalUser.hivOther || "");
    setLastTestedDate(finalUser.lastTestedDate || "");
    
    // -------------------- Medication History --------------------
    setAdditionalHistory(normalizeSelectionList(finalUser.additionalHealthHistory, OTHER_ALIAS));
    setAdditionalOther(finalUser.additionalOther || "");
    setContraceptionOtherText(finalUser.contraceptionOther || "");
    setLeftBox(normalizeSelectionList(finalUser.pastSurgeries, SURGERY_ALIASES));
    setLeftBoxOther(finalUser.surgeryOther || "");
    setRightBoxText(finalUser.currentMedications || "");
    
    // -------------------- Emergency Contact --------------------
    const isAssistedAccount = userData.assistanceRequired === "yes";
    setIsAssistedAccount(isAssistedAccount);
    
    let emergencyName = finalUser.emergencyContact?.name || finalUser.emergencyContactName || "";
    let emergencyContact = getLast10Digits(finalUser.emergencyContact?.contact || finalUser.emergencyContactNumber || "");
    let emergencyRelationship = "";
    let emergencyRelationshipOther = "";
    
    // Prioritize nested emergencyContact structure for all fields
    if (finalUser.emergencyContact?.name) {
      emergencyName = finalUser.emergencyContact.name;
    }
    if (finalUser.emergencyContact?.contact) {
      emergencyContact = getLast10Digits(finalUser.emergencyContact.contact);
    }
    
    // If assisted account and emergency contact fields are empty, prefill with guardian/accompanist data
    if (isAssistedAccount) {
      const reasonForAssistance = userData.reasonForAssistance || [];
      const isMinor = Array.isArray(reasonForAssistance)
        ? reasonForAssistance.includes("minor")
        : reasonForAssistance === "minor";
        
      if (!emergencyName && !emergencyContact) {
        if (isMinor) {
          emergencyName = userData.guardianFullName || "";
          emergencyContact = userData.guardianContact || "";
          emergencyRelationship = "Guardian";
        } else {
          emergencyName = userData.guardianFullName || "";
          emergencyContact = userData.guardianContact || "";
          emergencyRelationship = "Other";
          emergencyRelationshipOther = userData.guardianRelationship || "";
        }
      }
    }
    
    setEmergencyName(emergencyName);
    setEmergencyContact(emergencyContact);
    
    // Prioritize nested emergencyContact.relationship over flat field
    const existingRelationship = finalUser.emergencyContact?.relationship || finalUser.emergencyContactRelationship;
    const isExistingRelationshipOption = ["Parent","Partner","Guardian","Sibling","Relative","Other"].includes(existingRelationship);
    
    if (existingRelationship) {
      if (isExistingRelationshipOption) {
        setEmergencyRelationship(existingRelationship);
      } else {
        setEmergencyRelationship("Other");
        setEmergencyRelationshipOther(existingRelationship);
      }
    } else if (finalUser.emergencyContactRelationshipOther) {
      setEmergencyRelationshipOther(finalUser.emergencyContactRelationshipOther);
    } else {
      setEmergencyRelationship("");
    }
    
    if (finalUser.emergencyContactRelationshipOther) {
      setEmergencyRelationshipOther(finalUser.emergencyContactRelationshipOther);
    } else if (!isExistingRelationshipOption && existingRelationship) {
      setEmergencyRelationshipOther(existingRelationship);
    } else if (emergencyRelationshipOther) {
      setEmergencyRelationshipOther(emergencyRelationshipOther);
    }
    
    if (finalUser.personalInformation?.sex) {
      setUserSex(finalUser.personalInformation.sex.toLowerCase());
    } else if (finalUser.sex) {
      setUserSex(finalUser.sex.toLowerCase());
    }
    
    // Sync localStorage with the merged/fresh data to ensure structure is correct
    localStorage.setItem("user", JSON.stringify(finalUser));
    
    // Set two factor enabled state
    // For new accounts, if twoFactorEnabled field doesn't exist, default to true
    if (finalUser.twoFactorEnabled === undefined || finalUser.twoFactorEnabled === null) {
      setTwoFactorEnabled(true);
    } else if (typeof finalUser.twoFactorEnabled === "boolean") {
      setTwoFactorEnabled(finalUser.twoFactorEnabled);
    }

    // Set createdAt and lastLogin timestamps
    setCreatedAt(finalUser.createdAt || null);
    setLastLogin(finalUser.lastLogin || null);

    // Fetch health records and appointments with caching
    if (finalUser.uid) {
      try {
        // Firestore optimization: Check cache first
        let allAppts = cachedAppointmentsRef.current;

        if (!allAppts) {
          allAppts = await appointmentService.getUserAppointments(finalUser.uid);
          cachedAppointmentsRef.current = allAppts;
        }

        // Health records = completed appointments
        const completedAppts = allAppts.filter(a => a.status === 'COMPLETED');
        setHealthRecordsCount(completedAppts.length);

        // Confirmed appointments = confirmed
        const confirmedAppts = allAppts.filter(a => a.status === 'CONFIRMED');
        setConfirmedAppointmentsCount(confirmedAppts.length);
      } catch (err) {
        console.error("Error fetching counts:", err);
      }
    }
  };

  // Firestore optimization: Invalidate appointment cache when needed
  const invalidateAppointmentCache = () => {
    cachedAppointmentsRef.current = null;
  };

  // Firestore optimization: Invalidate user cache when needed
  const invalidateUserCache = () => {
    sharedUserDataCacheRef.current = null;
  };

  // Firestore optimization: Reset session recovery flag when needed
  const resetSessionRecovery = () => {
    sessionRecoveredRef.current = false;
  };

  // Firestore optimization: Invalidate location cache when needed
  const invalidateLocationCache = () => {
    locationCacheRef.current = {
      regions: null,
      provinces: null,
      cities: null,
      barangays: null
    };
  };

  // Load user data and construct display fields
  useEffect(() => {
    let unsubscribeNotifs = null;

    const loadUserDataWithNotifications = async () => {
      await loadUserData();
      setIsLoading(false);
      setHasLoaded(true);

      const userFromState = location.state?.user;
      const userFromStorage = getStoredUser();
      let finalUser = userFromState || userFromStorage;

      // Real-time listener for unread notifications
      if (finalUser?.uid) {
        unsubscribeNotifs = notificationService.getUserNotificationsListener(
          finalUser.uid,
          (notifications) => {
            const unreadCount = notifications.filter(n => n.status === "unread").length;
            setUnreadNotificationsCount(unreadCount);
          }
        );
      }
    };

    loadUserDataWithNotifications();

    // Cleanup listener when component unmounts or uid changes
    return () => {
      if (unsubscribeNotifs) unsubscribeNotifs();
    };
  }, [location.state?.user?.uid, location.state?.user?.email]); // Optimized dependencies to prevent unnecessary re-runs

  // ---- Real-time session list subscription ----
  useEffect(() => {
    const userFromState = location.state?.user;
    const userFromStorage = getStoredUser();
    const uid = userFromState?.uid || userFromStorage?.uid;
    if (!uid) return;

    setSessionsLoading(true);
    const unsubscribeSessions = sessionService.watchSessions(uid, (sessions) => {
      setActiveSessions(sessions);
      setSessionsLoading(false);
    });

    return () => unsubscribeSessions();
  }, [location.state?.user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Current-session revocation detection ----
  // If another device revokes this session, sign out and show a message.
  useEffect(() => {
    const userFromState = location.state?.user;
    const userFromStorage = getStoredUser();
    const uid = userFromState?.uid || userFromStorage?.uid;
    if (!uid) return;

    const unsubscribeRevocation = sessionService.watchCurrentSession(uid, async () => {
      setSessionRevoked(true);
      sessionService.clearCurrentSession();
      try {
        await signOut(auth);
      } catch (_) {}
      localStorage.clear();
      navigate("/login", {
        replace: true,
        state: {
          message: "Your session has been revoked. Please sign in again.",
          type: "error",
        },
      });
    });

    return () => unsubscribeRevocation();
  }, [location.state?.user?.uid, navigate]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // For "Other" option in emergency contact relationship dropdown
  useEffect(() => {
    if (!emergencyRelationship) {
      setIsOtherTyping(false);
      return;
    }
    setIsOtherTyping(!RELATIONSHIP_OPTIONS.includes(emergencyRelationship));
  }, [emergencyRelationship]);

  // Switch away from emergency tab if user is an assisted account
  useEffect(() => {
    if (isAssistedAccount && activeTab === "emergency") {
      setActiveTab("personal");
    }
  }, [isAssistedAccount, activeTab]);

  // Consolidated location data loading with guard conditions
  useEffect(() => {
    const controller = new AbortController();
    
    const loadRegions = async () => {
      // Guard: Skip if already loaded
      if (regions.length > 0) return;
      
      setIsRegionsLoading(true);
      try {
        let data = locationCacheRef.current.regions;
        if (!data) {
          data = await psgcService.getRegions();
          locationCacheRef.current.regions = data;
        }
        setRegions(normalizeList(data));
      } catch (error) {
        console.error("Failed to load regions:", error);
        setRegions([]);
      } finally {
        setIsRegionsLoading(false);
      }
    };
    
    loadRegions();
    return () => controller.abort();
  }, []);

  // Consolidated province loading with selection logic
  useEffect(() => {
    if (!personalEdit) return;
    
    const controller = new AbortController();
    
    const loadProvinces = async () => {
      // Guard: Skip if no region selected
      if (!selectedRegionId) {
        setProvinces([]);
        setSelectedProvinceId(null);
        setCities([]);
        setSelectedCityId(null);
        setBarangays([]);
        setSelectedBarangayId(null);
        return;
      }
      
      // Guard: Skip if already loaded for this region
      const cacheKey = `provinces_${selectedRegionId}`;
      if (locationCacheRef.current.provinces?.[cacheKey] && provinces.length > 0) {
        // Still need to check for auto-selection
        if (!selectedProvinceId && tempPersonalData.province) {
          const provinceMatch = findByName(provinces, tempPersonalData.province);
          if (provinceMatch) setSelectedProvinceId(provinceMatch.psgc_id);
        }
        return;
      }
      
      setIsProvincesLoading(true);
      try {
        let data = locationCacheRef.current.provinces?.[cacheKey];
        if (!data) {
          data = await psgcService.getProvincesByRegion(selectedRegionId);
          if (!locationCacheRef.current.provinces) {
            locationCacheRef.current.provinces = {};
          }
          locationCacheRef.current.provinces[cacheKey] = data;
        }
        const normalizedProvinces = normalizeList(data);
        setProvinces(normalizedProvinces);
        
        // Auto-select province if tempPersonalData has it
        if (!selectedProvinceId && tempPersonalData.province) {
          const provinceMatch = findByName(normalizedProvinces, tempPersonalData.province);
          if (provinceMatch) setSelectedProvinceId(provinceMatch.psgc_id);
        }
      } catch (error) {
        console.error("Failed to load provinces:", error);
        setProvinces([]);
      } finally {
        setIsProvincesLoading(false);
      }
    };
    
    loadProvinces();
    return () => controller.abort();
  }, [personalEdit, selectedRegionId]);

  // Consolidated city loading with selection logic
  useEffect(() => {
    if (!personalEdit) return;
    
    const controller = new AbortController();
    
    const loadCities = async () => {
      // Guard: Skip if no province selected
      if (!selectedProvinceId) {
        setCities([]);
        setSelectedCityId(null);
        setBarangays([]);
        setSelectedBarangayId(null);
        return;
      }
      
      // Guard: Skip if already loaded for this province
      const cacheKey = `cities_${selectedProvinceId}`;
      if (locationCacheRef.current.cities?.[cacheKey] && cities.length > 0) {
        // Still need to check for auto-selection
        if (!selectedCityId && tempPersonalData.city) {
          const cityMatch = findByName(cities, tempPersonalData.city);
          if (cityMatch) setSelectedCityId(cityMatch.psgc_id);
        }
        return;
      }
      
      setIsCitiesLoading(true);
      try {
        let data = locationCacheRef.current.cities?.[cacheKey];
        if (!data) {
          data = await psgcService.getCitiesByProvince(selectedProvinceId);
          if (!locationCacheRef.current.cities) {
            locationCacheRef.current.cities = {};
          }
          locationCacheRef.current.cities[cacheKey] = data;
        }
        const normalizedCities = normalizeList(data);
        setCities(normalizedCities);
        
        // Auto-select city if tempPersonalData has it
        if (!selectedCityId && tempPersonalData.city) {
          const cityMatch = findByName(normalizedCities, tempPersonalData.city);
          if (cityMatch) setSelectedCityId(cityMatch.psgc_id);
        }
      } catch (error) {
        console.error("Failed to load cities:", error);
        setCities([]);
      } finally {
        setIsCitiesLoading(false);
      }
    };
    
    loadCities();
    return () => controller.abort();
  }, [personalEdit, selectedProvinceId]);

  // Consolidated barangay loading with selection logic
  useEffect(() => {
    if (!personalEdit) return;
    
    const controller = new AbortController();
    
    const loadBarangays = async () => {
      // Guard: Skip if no city selected
      if (!selectedCityId) {
        setBarangays([]);
        setSelectedBarangayId(null);
        return;
      }
      
      // Guard: Skip if already loaded for this city
      const cacheKey = `barangays_${selectedCityId}`;
      if (locationCacheRef.current.barangays?.[cacheKey] && barangays.length > 0) {
        // Still need to check for auto-selection
        if (!selectedBarangayId && tempPersonalData.barangay) {
          const barangayMatch = findByName(barangays, tempPersonalData.barangay);
          if (barangayMatch) setSelectedBarangayId(barangayMatch.psgc_id);
        }
        return;
      }
      
      setIsBarangaysLoading(true);
      try {
        let data = locationCacheRef.current.barangays?.[cacheKey];
        if (!data) {
          data = await psgcService.getBarangaysByCity(selectedCityId);
          if (!locationCacheRef.current.barangays) {
            locationCacheRef.current.barangays = {};
          }
          locationCacheRef.current.barangays[cacheKey] = data;
        }
        const normalizedBarangays = normalizeList(data);
        setBarangays(normalizedBarangays);
        
        // Auto-select barangay if tempPersonalData has it
        if (!selectedBarangayId && tempPersonalData.barangay) {
          const barangayMatch = findByName(normalizedBarangays, tempPersonalData.barangay);
          if (barangayMatch) setSelectedBarangayId(barangayMatch.psgc_id);
        }
      } catch (error) {
        console.error("Failed to load barangays:", error);
        setBarangays([]);
      } finally {
        setIsBarangaysLoading(false);
      }
    };
    
    loadBarangays();
    return () => controller.abort();
  }, [personalEdit, selectedCityId]);

  // Handle region auto-selection from tempPersonalData
  useEffect(() => {
    if (!personalEdit || !regions.length || selectedRegionId) return;
    if (tempPersonalData.region) {
      const regionMatch = findByName(regions, tempPersonalData.region);
      if (regionMatch) setSelectedRegionId(regionMatch.psgc_id);
    }
  }, [personalEdit, regions, tempPersonalData.region, selectedRegionId]);

  // Monitor assisted relationship changes for Others functionality
  useEffect(() => {
    setIsAssistedOtherTyping(!RELATIONSHIP_OPTIONS.includes(tempAssistedData.relationship) && tempAssistedData.relationship !== "");
  }, [tempAssistedData.relationship]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeoutsRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
      if (sharedBatchingRefs.saveTimeout) {
        clearTimeout(sharedBatchingRefs.saveTimeout);
      }
    };
  }, []);

  /* ======================
     SESSION MANAGEMENT HANDLERS
  ====================== */

  /**
   * Revoke a single session by calling the Cloud Function.
   * If the revoked session is the current one, the watchCurrentSession listener
   * will fire and handle sign-out automatically.
   */
  const handleRevokeSession = async (sessionId) => {
    if (revokeLoadingId) return; // prevent double-click
    setRevokeLoadingId(sessionId);
    try {
      await apiRevokeSession(sessionId);
      // The real-time watchSessions listener will update activeSessions automatically.
      // If it was the current session, watchCurrentSession fires sign-out.
    } catch (err) {
      console.error("[ProfileAccounts] revokeSession error:", err);
      showBanner(err.message || "Failed to revoke session. Please try again.", "error");
    } finally {
      setRevokeLoadingId(null);
    }
  };

  /** Show the confirmation dialog before logging out all sessions. */
  const handleLogoutAllClick = () => setShowLogoutAllConfirm(true);

  /**
   * Confirm: revoke all sessions via the Cloud Function, then sign the current
   * browser out and redirect to login.
   */
  const handleLogoutAllConfirm = async () => {
    setShowLogoutAllConfirm(false);
    setLogoutAllLoading(true);
    try {
      await apiLogoutAllSessions();
      // Cloud Function has revoked all tokens and marked all sessions revoked.
      // Now sign out the current browser.
      sessionService.clearCurrentSession();
      sessionStorage.setItem("logoutInProgress", "true");
      await signOut(auth);
      localStorage.clear();
      navigate("/login", {
        replace: true,
        state: {
          message: "You have been signed out of all sessions.",
          type: "success",
        },
      });
    } catch (err) {
      console.error("[ProfileAccounts] logoutAllSessions error:", err);
      showBanner(err.message || "Failed to log out all sessions. Please try again.", "error");
      setLogoutAllLoading(false);
    }
  };

  /* ======================
     HANDLERS (UNCHANGED)
  ====================== */
  const handlePersonalEdit = () => {
    setPersonalEdit(true);
    setTempPersonalData({ ...personalData });
    setSelectedRegionId("");
    setSelectedProvinceId("");
    setSelectedCityId("");
    setSelectedBarangayId("");
  };

  const handlePersonalCancel = () => {
    setPersonalEdit(false);
    setTempPersonalData({ ...personalData });
    setErrors({}); // Clear all errors when canceling
    setSelectedRegionId("");
    setSelectedProvinceId("");
    setSelectedCityId("");
    setSelectedBarangayId("");
    // Clear validation cache on cancel
    clearAllValidationCache();
  };

  const savePendingProfileChange = (payload) => {
    localStorage.setItem("pendingProfileChange", JSON.stringify(payload));
  };

  const handlePersonalSave = async () => {
    try {
      // Validation Check
      const usernameError = validateUsername(tempPersonalData.username, true);
      const emailError = validateEmail(tempPersonalData.email, true);
      const contactError = validateContact(tempPersonalData.contact, true);

      // Address Validation
      let addressErrors = {};
      if (tempPersonalData.region === "") addressErrors.region = "Region is required";
      if (tempPersonalData.province === "") addressErrors.province = "Province is required";
      if (tempPersonalData.city === "") addressErrors.city = "City is required";
      if (tempPersonalData.barangay === "") addressErrors.barangay = "Barangay is required";

      if (Object.keys(addressErrors).length > 0) {
        setErrors((p) => ({ ...p, ...addressErrors }));
        return;
      }

      // Explicitly check for duplicates and await results
      let dupUsernameError = "";
      let dupEmailError = "";
      let dupContactError = "";

      if (tempPersonalData.username !== originalPersonalData.username) {
        dupUsernameError = await handleUsernameBlur(tempPersonalData.username);
      }
      if (tempPersonalData.email !== originalPersonalData.email) {
        dupEmailError = await handleEmailBlur(tempPersonalData.email);
      }
      if (tempPersonalData.contact !== originalPersonalData.contact) {
        dupContactError = await handleContactBlur(tempPersonalData.contact);
      }

      if (usernameError || emailError || contactError || dupUsernameError || dupEmailError || dupContactError || hasValidationErrors()) {
        return;
      }

      const loggedInUser = getStoredUser();

    // Enhanced session validation - check for uid or email as fallback
    if (!loggedInUser || (!loggedInUser?.uid && !loggedInUser?.email)) {
      console.warn('Session validation failed - missing uid and email:', loggedInUser);
      showBanner("Session expired. Please log in again.", "error");
      return;
    }

    // Firestore optimization: Perform session recovery only once per session
    if (!loggedInUser?.uid && loggedInUser?.email && !sessionRecoveredRef.current) {
      try {
        const fetchedUser = await userService.getUserByEmail(loggedInUser.email);
        if (fetchedUser && fetchedUser.id) {
          loggedInUser.uid = fetchedUser.id;
          localStorage.setItem("user", JSON.stringify(loggedInUser));
          sessionRecoveredRef.current = true;
          // Update cache with recovered user data
          sharedUserDataCacheRef.current = loggedInUser;
        } else {
          showBanner("Session expired. Please log in again.", "error");
          return;
        }
      } catch (error) {
        console.error('Failed to fetch user data for session recovery:', error);
        showBanner("Session expired. Please log in again.", "error");
        return;
      }
    }

    const requiresVerification =
      tempPersonalData.email !== originalPersonalData.email ||
      tempPersonalData.contact !== originalPersonalData.contact ||
      tempPersonalData.username !== originalPersonalData.username;

    const update = {};
    if (tempPersonalData.region !== originalPersonalData.region) update.region = tempPersonalData.region;
    if (tempPersonalData.province !== originalPersonalData.province) update.province = tempPersonalData.province;
    if (tempPersonalData.city !== originalPersonalData.city) update.city = tempPersonalData.city;
    if (tempPersonalData.barangay !== originalPersonalData.barangay) update.barangay = tempPersonalData.barangay;

    if (requiresVerification) {
        const pendingProfileChange = {
          uid: loggedInUser.uid,
          currentEmail: loggedInUser.email,
          currentContact: loggedInUser.contact,
          originalData: originalPersonalData,
          updatedData: tempPersonalData,
        };
        savePendingProfileChange(pendingProfileChange);

        // Set changeType based on what's being changed
        const hasEmailChange = tempPersonalData.email !== originalPersonalData.email;
        const hasContactChange = tempPersonalData.contact !== originalPersonalData.contact;
        
        if (hasContactChange && !hasEmailChange) {
          setUserChangeType('contact');
        } else if (hasEmailChange && !hasContactChange) {
          setUserChangeType('email');
        } else {
          setUserChangeType('profile');
        }
        
        setUserChangeData(pendingProfileChange);
        setShowUserChangeOtpModal(true);
        return;
      }

    if (Object.keys(update).length === 0) {
      showBanner("No changes to update.", "info");
      return;
    }

    // Use immediate save for critical profile updates
    const mergedUser = await immediateUpdateUser(update, loggedInUser);
    
    if (mergedUser) {
      const processedUser = processUserData(mergedUser);
      setPersonalData(processedUser);
      setTempPersonalData(processedUser);
      setOriginalPersonalData(processedUser);
      setPersonalEdit(false);

      // Firestore optimization: Invalidate appointment cache
      invalidateAppointmentCache();

      // Firestore optimization: Invalidate location cache if address changed
      if (update.region || update.province || update.city || update.barangay) {
        invalidateLocationCache();
      }

      // Clear validation cache after successful save
      clearAllValidationCache();

      showBanner("Profile updated successfully!", "success");
    }
  } catch (err) {
    console.error(err);
    showBanner(err.message || "Server error. Please try again.", "error");
  }
};

  // Account Information Handlers
  const handleAccountEdit = () => {
    setAccountEdit(true);
  };

  const handleAccountCancel = () => {
    setAccountEdit(false);
    setTempPersonalData({ ...personalData });
    setErrors({});
  };

  const handleAccountSave = async () => {
    try {
      // Validation Check
      const emailError = validateEmail(tempPersonalData.email, true);
      const contactError = validateContact(tempPersonalData.contact, true);

      // Explicitly check for duplicates and await results
      let dupEmailError = "";
      let dupContactError = "";

      if (tempPersonalData.email !== originalPersonalData.email) {
        dupEmailError = await handleEmailBlur(tempPersonalData.email);
      }
      if (tempPersonalData.contact !== originalPersonalData.contact) {
        dupContactError = await handleContactBlur(tempPersonalData.contact);
      }
      
      if (emailError || contactError || dupEmailError || dupContactError || hasValidationErrors()) {
        return;
      }

      const loggedInUser = getStoredUser();

      // Enhanced session validation - check for uid or email as fallback
      if (!loggedInUser || (!loggedInUser?.uid && !loggedInUser?.email)) {
        console.warn('Session validation failed - missing uid and email:', loggedInUser);
        showBanner("Session expired. Please log in again.", "error");
        return;
      }

      // Firestore optimization: Perform session recovery only once per session
      if (!loggedInUser?.uid && loggedInUser?.email && !sessionRecoveredRef.current) {
        try {
          const fetchedUser = await userService.getUserByEmail(loggedInUser.email);
          if (fetchedUser && fetchedUser.id) {
            loggedInUser.uid = fetchedUser.id;
            localStorage.setItem("user", JSON.stringify(loggedInUser));
            sessionRecoveredRef.current = true;
            // Update cache with recovered user data
            sharedUserDataCacheRef.current = loggedInUser;
          } else {
            showBanner("Session expired. Please log in again.", "error");
            return;
          }
        } catch (error) {
          console.error('Failed to fetch user data for session recovery:', error);
          showBanner("Session expired. Please log in again.", "error");
          return;
        }
      }

      const requiresVerification =
        tempPersonalData.email !== originalPersonalData.email ||
        tempPersonalData.contact !== originalPersonalData.contact;

      if (requiresVerification) {
        const pendingProfileChange = {
          uid: loggedInUser.uid,
          currentEmail: loggedInUser.email,
          currentContact: loggedInUser.contact,
          originalData: originalPersonalData,
          updatedData: tempPersonalData,
        };
        savePendingProfileChange(pendingProfileChange);

        setUserChangeType('profile');
        setUserChangeData(pendingProfileChange);
        setShowUserChangeOtpModal(true);
        return;
      }

      // Update Firestore
      const update = {};
      if (tempPersonalData.email !== originalPersonalData.email) {
        update.email = tempPersonalData.email;
        update.emailLower = tempPersonalData.email.toLowerCase();
      }
      if (tempPersonalData.contact !== originalPersonalData.contact) {
        update.contact = tempPersonalData.contact;
        update.phone = `+63${tempPersonalData.contact}`;
        update.phoneLast10 = tempPersonalData.contact;
      }

      // Use immediate save for critical account updates
      const mergedUser = await immediateUpdateUser(update, loggedInUser);
      
      if (mergedUser) {
        const processedUser = processUserData(mergedUser);
        setPersonalData(processedUser);
        setTempPersonalData(processedUser);
        setOriginalPersonalData(processedUser);
        setAccountEdit(false);

        // Firestore optimization: Invalidate appointment cache
        invalidateAppointmentCache();

        // Firestore optimization: Invalidate location cache if address changed
        if (update.region || update.province || update.city || update.barangay) {
          invalidateLocationCache();
        }

        // Clear validation cache after successful save
        clearAllValidationCache();

        showBanner("Account information updated successfully!", "success");
      }
    } catch (err) {
      console.error(err);
      showBanner(err.message || "Server error. Please try again.", "error");
    }
  };

  /* ======================
     PERSONAL VALIDATION
     (UNCHANGED)
  ====================== */


  // Only validate if value has changed from original
  const hasValueChanged = (fieldName, newValue) => newValue !== originalPersonalData[fieldName];

  const validateUsername = (value, checkIfChanged = true) => {
    if (checkIfChanged && !hasValueChanged('username', value)) return "";
    if (!value) return "Username is required";
    if (value.length < 8) return "Username must be at least 8 characters";
    if (value.length > 50) return "Username must be at most 50 characters";
    if (/\u0000/.test(value)) return "Username contains invalid characters";
    if (/\s/.test(value)) return "Spaces are not allowed in username";
    return "";
  };

  const validateEmail = (value, checkIfChanged = true) => {
    if (checkIfChanged && !hasValueChanged('email', value)) return "";
    if (!value) return "Email is required";
    if (value.length > 254) return "Email must be at most 254 characters";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Invalid email format";
    return "";
  };

  const validateContact = (value, checkIfChanged = true) => {
    if (checkIfChanged && !hasValueChanged('contact', value)) return "";
    if (!value) return "Contact number is required";
    // Allow 10 digits (9xxxxxxxxx)
    if (!/^9\d{9}$/.test(value)) return "Contact number must be 10 digits starting with 9";
    return "";
  };

  const handlePersonalInputChange = (field, value) => {
    const editableFields = new Set(["username", "email", "contact", "region", "province", "city", "barangay"]);
    if (!editableFields.has(field)) return;

    if (field === "email" || field === "username") {
      value = value.replace(/\s+/g, "");
    }

    // Restrict input to numbers only for contact
    if (field === "contact" && !/^\d*$/.test(value)) return;
    // Max length 10 for contact
    if (field === "contact" && value.length > 10) return;

    if (field === "region") {
      const region = findById(regions, value);
      setSelectedRegionId(value);
      setSelectedProvinceId("");
      setSelectedCityId("");
      setSelectedBarangayId("");
      setProvinces([]);
      setCities([]);
      setBarangays([]);
      setTempPersonalData((prev) => ({
        ...prev,
        region: region?.name || "",
        province: "",
        city: "",
        barangay: ""
      }));
      return;
    }

    if (field === "province") {
      const province = findById(provinces, value);
      setSelectedProvinceId(value);
      setSelectedCityId("");
      setSelectedBarangayId("");
      setCities([]);
      setBarangays([]);
      setTempPersonalData((prev) => ({
        ...prev,
        province: province?.name || "",
        city: "",
        barangay: ""
      }));
      return;
    }

    if (field === "city") {
      const city = findById(cities, value);
      setSelectedCityId(value);
      setSelectedBarangayId("");
      setBarangays([]);
      setTempPersonalData((prev) => ({
        ...prev,
        city: city?.name || "",
        barangay: ""
      }));
      return;
    }

    if (field === "barangay") {
      const barangay = findById(barangays, value);
      setSelectedBarangayId(value);
      setTempPersonalData((prev) => ({
        ...prev,
        barangay: barangay?.name || ""
      }));
      return;
    }

    setTempPersonalData((prev) => ({ ...prev, [field]: value }));

    // Live Validation: Only validate format. 
    // Duplication errors from blur handlers will persist until the value changes again.
    let errorMsg = "";
    if (field === "username") {
      errorMsg = validateUsername(value);
    } else if (field === "email") {
      errorMsg = validateEmail(value);
    } else if (field === "contact") {
      errorMsg = validateContact(value);
    }

    // Set the error state for the specific field. 
    // If there's a format error, show it. 
    // If no format error, but there was a duplication error, keep the duplication error 
    // until the value is changed or re-blurred.
    setErrors((prev) => {
      const currentErr = prev[field];
      const isDuplicationError = currentErr === "Email already taken" || 
                                currentErr === "Contact number already taken" || 
                                currentErr === "Username is taken";
      
      const isMinor = personalData.reasonForAssistance?.includes('minor');

      if (errorMsg) {
        return { ...prev, [field]: errorMsg };
      } else {
        const newErrors = { ...prev, [field]: null };
        
        // Cross-validation for email if it's a minor account
        if (field === "email" && isMinor) {
          if (value && value.toLowerCase() === tempAssistedData.email?.toLowerCase()) {
            newErrors.email = "Email cannot be the same as guardian email.";
          }
        }
        
        // Cross-validation for contact number
        if (field === "contact") {
          if (value && value === tempAssistedData.contact) {
            newErrors.contact = isMinor 
              ? "Contact number cannot be the same as guardian contact number."
              : "Contact number cannot be the same as accompanist contact number.";
          }
        }

        return newErrors;
      }
    });
  };

  // Account Information Input Change Handler
  const handleAccountInputChange = (field, value) => {
    const editableFields = new Set(["email", "contact"]);
    if (!editableFields.has(field)) return;

    if (field === "email") {
      value = value.replace(/\s+/g, "");
    }

    // Restrict input to numbers only for contact
    if (field === "contact" && !/^\d*$/.test(value)) return;
    // Max length 10 for contact
    if (field === "contact" && value.length > 10) return;

    setTempPersonalData((prev) => ({ ...prev, [field]: value }));

    // Live Validation: Only validate format.
    let errorMsg = "";
    if (field === "email") {
      errorMsg = validateEmail(value);
    } else if (field === "contact") {
      errorMsg = validateContact(value);
    }

    // Set the error state for the specific field (clear if no format error, duplication handled on blur)
    setErrors((prev) => {
      const newErrors = { ...prev, [field]: errorMsg || null };
      
      const isMinor = personalData.reasonForAssistance?.includes('minor');

      // Cross-validation for email
      if (field === "email") {
        if (isMinor && value && value.toLowerCase() === tempAssistedData.email?.toLowerCase()) {
          newErrors.email = "Email cannot be the same as guardian email.";
        }
      }

      // Cross-validation for contact number
      if (field === "contact") {
        if (value && value === tempAssistedData.contact) {
          newErrors.contact = isMinor 
            ? "Contact number cannot be the same as guardian contact number."
            : "Contact number cannot be the same as accompanist contact number.";
        }
      }
      
      return newErrors;
    });
  };

  // Assisted Account Handlers
  const handleAssistedEdit = () => {
    setAssistedEdit(true);
    // Set Others typing state based on current relationship value
    setIsAssistedOtherTyping(!RELATIONSHIP_OPTIONS.includes(tempAssistedData.relationship) && tempAssistedData.relationship !== "");
  };

  const handleAssistedCancel = () => {
    setAssistedEdit(false);
    setTempAssistedData({ ...originalAssistedData });
    setErrors({});
    setIsAssistedOtherTyping(false);
  };

  const handleAssistedInputChange = (field, value) => {
    if (field === "contact") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    
    setTempAssistedData(prev => ({ ...prev, [field]: value }));
    
    // Live validation for uniqueness
    setErrors(prev => {
      const newErrors = { ...prev };
      const isMinor = personalData.reasonForAssistance?.includes('minor');

      if (field === "contact") {
        if (value && value === tempPersonalData.contact) {
          newErrors.assistedContact = "Contact number cannot be the same as user contact number.";
        } else {
          delete newErrors.assistedContact;
        }
      }

      if (field === "email" && isMinor) {
        if (value && value.toLowerCase() === tempPersonalData.email?.toLowerCase()) {
          newErrors.assistedEmail = "Email cannot be the same as user email.";
        } else {
          delete newErrors.assistedEmail;
        }
      }
      
      return newErrors;
    });
  };

  const handleAssistedSave = async () => {
    try {
      const isMinor = personalData.reasonForAssistance?.includes('minor');
      
      // Basic validation
      if (!tempAssistedData.fullName.trim() || !tempAssistedData.relationship.trim() || !tempAssistedData.contact.trim()) {
        showBanner("All fields are required.", "error");
        return;
      }
      

      if (errors.assistedContact || (isMinor && errors.assistedEmail)) return;

      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      // Check if guardian email has changed (only for minors)
      const currentEmail = originalAssistedData.email;
      const newEmail = tempAssistedData.email;
      const emailChanged = isMinor && currentEmail !== newEmail;

      if (emailChanged) {
        // Only email changed - use modal for OTP verification for email change
        const pendingAssistedChange = {
          uid: loggedInUser.uid,
          currentEmail: currentEmail,
          newEmail: newEmail,
          isMinor: isMinor,
          fullName: tempAssistedData.fullName,
          relationship: tempAssistedData.relationship,
          currentUserEmail: loggedInUser.email || personalData.email,
          originalData: originalAssistedData,
          updatedData: tempAssistedData,
          changeType: "email"
        };
        
        localStorage.setItem("pendingAssistedChange", JSON.stringify(pendingAssistedChange));
        
        setUserChangeType('assisted-email');
        setUserChangeData(pendingAssistedChange);
        setShowUserChangeOtpModal(true);
        return;
      }

      // Proceed with normal update for all fields (including contact without OTP)
      const update = {};

      if (isMinor) {
        // Pass field names without dot notation - buildUserUpdateModule will create nested structure
        update.guardianFullName = tempAssistedData.fullName;
        update.guardianRelationshipToUser = tempAssistedData.relationship;
        // Add +63 prefix to contact number for Firestore
        update.guardianContactNumber = tempAssistedData.contact ? `+63${tempAssistedData.contact}` : "";
        update.guardianAddress = tempAssistedData.address;
      } else {
        // Pass field names without dot notation - buildUserUpdateModule will create nested structure
        update.accompanistFullName = tempAssistedData.fullName;
        update.accompanistRelationshipToUser = tempAssistedData.relationship;
        // Add +63 prefix to contact number for Firestore
        update.accompanistContactNumber = tempAssistedData.contact ? `+63${tempAssistedData.contact}` : "";
        update.accompanistAddress = tempAssistedData.address;
      }

      if (Object.keys(update).length > 0) {
        // Use immediate save for assisted account updates
        const mergedUser = await immediateUpdateUser(update, loggedInUser);

        if (mergedUser) {
          localStorage.setItem("user", JSON.stringify(mergedUser));
          setOriginalAssistedData({ ...tempAssistedData });
          // Reload user data to refresh the display
          await loadUserData();
        }
      }

      setAssistedEdit(false);
      showBanner("Assisted account information updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update assisted account information.", "error");
    }
  };
  
  // ---------------- Health History Helpers and Handlers ----------------
  
  const ensureOtherSelected = (list, otherText) => {
    if (otherText && !list.includes("Others (Please Specify)")) {
      return [...list, "Others (Please Specify)"];
    }
    return list;
  };

  const normalizeSelectionList = (list, aliasMap) => {
    const map = Object.keys(aliasMap).reduce((acc, key) => {
      acc[key.toLowerCase()] = aliasMap[key];
      return acc;
    }, {});
    return (Array.isArray(list) ? list : [])
      .map((item) => {
        const trimmed = String(item || "").trim();
        if (!trimmed) return "";
        return map[trimmed.toLowerCase()] || trimmed;
      })
      .filter(Boolean);
  };

  const toggleItem = (item, list, setter, otherSetter) => {
    if (!(healthEditing || additionalEditing || clinicalEditing)) return;
    if (list.includes(item)) {
      setter(list.filter((i) => i !== item));
      if (item === "Others (Please Specify)" && otherSetter) otherSetter("");
      if (item === "Hormonal Medications (Pills, Patches, Injections)" && otherSetter) otherSetter("");
      
      // Clear validation errors when pill is deselected
      if (healthEditing) {
        if (item === "Others (Please Specify)") {
          setHealthFieldErrors(prev => ({ ...prev, medicalOther: null }));
        } else if (item === "Hormonal Medications (Pills, Patches, Injections)") {
          setHealthFieldErrors(prev => ({ ...prev, hormonalMedicationText: null }));
        }
      } else if (hivEditing && item === "Others (Please Specify)") {
        setStiFieldErrors(prev => ({ ...prev, stiOther: null }));
      } else if (additionalEditing && item === "Others (Please Specify)") {
        setAdditionalFieldErrors(prev => ({ ...prev, contraceptionOtherText: null, leftBoxOther: null }));
      }
    } else {
      setter([...list, item]);
    }
  };

  const validateEmergencyContact = () => {
    const newErrors = {};
    if (!emergencyName.trim()) newErrors.name = "Name is required";
    const relationshipValue = isOtherTyping ? emergencyRelationshipOther : emergencyRelationship;
    if (!relationshipValue.trim()) newErrors.relationship = "Relationship is required";
    if (!emergencyContact.trim()) newErrors.contact = "Contact Number is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -------------------- EDIT / TOGGLE HANDLERS --------------------
  // Health & Lifestyle - store original values on edit
  const handleHealthEdit = () => {
    setOrigMedicalHistory([...medicalHistory]);
    setOrigMedicalOther(medicalOther);
    setOrigAllergies([...allergies]);
    setOrigAllergyOther(allergyOther);
    setOrigAlcoholUse(alcoholUse);
    setOrigSmoking(smoking);
    setOrigHormonalMedicationText(hormonalMedicationText);
    setHealthEditing(true);
  };
  
  // Cancel handler - restore original values and exit edit mode
  const handleHealthCancel = () => {
    setMedicalHistory(origMedicalHistory);
    setMedicalOther(origMedicalOther);
    setAllergies(origAllergies);
    setAllergyOther(origAllergyOther);
    setAlcoholUse(origAlcoholUse);
    setSmoking(origSmoking);
    setHormonalMedicationText(origHormonalMedicationText);
    setHealthEditing(false);
  };
  
    
  // HIV Status - store original values on edit
  const handleHivEdit = () => {
    setOrigHivSelection([...hivSelection]);
    setOrigStiOther(stiOther);
    setOrigHivLeftBox(hivLeftBox);
    setOrigHivRightBoxText(hivRightBoxText);
    setOrigLastTestedDate(lastTestedDate);
    setHivEditing(true);
  };
  
  // Cancel handler - restore original values and exit edit mode
  const handleHivCancel = () => {
    setHivSelection(origHivSelection);
    setStiOther(origStiOther);
    setHivLeftBox(origHivLeftBox);
    setHivRightBoxText(origHivRightBoxText);
    setLastTestedDate(origLastTestedDate);
    setHivEditing(false);
  };
  // Additional Health Info - store original values on edit
  const handleAdditionalEdit = () => {
    setOrigAdditionalHistory([...additionalHistory]);
    setOrigAdditionalOther(additionalOther);
    setOrigContraceptionOtherText(contraceptionOtherText);
    setOrigLeftBox([...leftBox]);
    setOrigLeftBoxOther(leftBoxOther);
    setOrigRightBoxText(rightBoxText);
    setAdditionalEditing(true);
  };
  
  // Cancel handler - restore original values and exit edit mode
  const handleAdditionalCancel = () => {
    setAdditionalHistory(origAdditionalHistory);
    setAdditionalOther(origAdditionalOther);
    setContraceptionOtherText(origContraceptionOtherText);
    setLeftBox(origLeftBox);
    setLeftBoxOther(origLeftBoxOther);
    setRightBoxText(origRightBoxText);
    setAdditionalEditing(false);
  };
  
  // Emergency Contact - store original values on edit
  const handleClinicalEdit = () => {
    setOrigEmergencyName(emergencyName);
    setOrigEmergencyRelationship(emergencyRelationship);
    setOrigEmergencyRelationshipOther(emergencyRelationshipOther);
    setOrigEmergencyContact(emergencyContact);
    setClinicalEditing(true);
  };
  
  // Cancel handler - restore original values and exit edit mode
  const handleClinicalCancel = () => {
    setEmergencyName(origEmergencyName);
    setEmergencyRelationship(origEmergencyRelationship);
    setEmergencyRelationshipOther(origEmergencyRelationshipOther);
    setEmergencyContact(origEmergencyContact);
    setClinicalEditing(false);
  };

  // Save handlers
  const handleHealthSave = async () => {
    try {
      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      // Validate "Others (Please Specify)" fields
      const newErrors = {};
      if (medicalHistory.includes("Others (Please Specify)") && !medicalOther.trim()) {
        newErrors.medicalOther = "Please specify the medical condition";
      }
      if (allergies.includes("Others (Please Specify)") && !allergyOther.trim()) {
        newErrors.allergyOther = "Please specify the allergy";
      }
      if (allergies.includes("Hormonal Medications (Pills, Patches, Injections)") && !hormonalMedicationText.trim()) {
        newErrors.hormonalMedicationText = "Please specify the hormonal medication";
      }

      if (Object.keys(newErrors).length > 0) {
        setHealthFieldErrors(newErrors);
        showBanner("Please fill in all required fields", "error");
        return;
      }

      const update = {
        medicalHistory: ensureOtherSelected(medicalHistory, medicalOther),
        medicalOther: medicalOther,
        allergies: ensureOtherSelected(allergies, allergyOther),
        allergyOther: allergyOther,
        alcoholUse: alcoholUse,
        smoking: smoking,
        hormonalMedications: hormonalMedicationText,
      };

      // Use batched update for health information
      await batchUpdateUser(update, loggedInUser);

      setHealthEditing(false);
      setHealthFieldErrors({});
      showBanner("Health information updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update health information.", "error");
    }
  };

  const handleHivSave = async () => {
    try {
      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      // Validate "Others (Please Specify)" field
      const newErrors = {};
      if (hivSelection.includes("Others (Please Specify)") && !stiOther.trim()) {
        newErrors.stiOther = "Please specify the STI/condition";
      }

      if (Object.keys(newErrors).length > 0) {
        setStiFieldErrors(newErrors);
        showBanner("Please fill in all required fields", "error");
        return;
      }

      const update = {
        stiHistory: ensureOtherSelected(hivSelection, stiOther),
        stiOther: stiOther,
        hivStatus: hivLeftBox,
        hivOther: hivRightBoxText,
        lastTestedDate: lastTestedDate,
      };

      // Use batched update for HIV information
      await batchUpdateUser(update, loggedInUser);

      setHivEditing(false);
      setStiFieldErrors({});
      showBanner("STI/HIV information updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update STI/HIV information.", "error");
    }
  };

  const handleAdditionalSave = async () => {
    try {
      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      // Validate "Others (Please Specify)" fields
      const newErrors = {};
      if (additionalHistory.includes("Others (Please Specify)") && !contraceptionOtherText.trim()) {
        newErrors.contraceptionOtherText = "Please specify the hormonal contraception";
      }
      if (leftBox.includes("Others (Please Specify)") && !leftBoxOther.trim()) {
        newErrors.leftBoxOther = "Please specify the surgery";
      }

      if (Object.keys(newErrors).length > 0) {
        setAdditionalFieldErrors(newErrors);
        showBanner("Please fill in all required fields", "error");
        return;
      }

      const update = {
        additionalHealthHistory: ensureOtherSelected(additionalHistory, additionalOther),
        additionalOther: additionalOther,
        hormonalMedications: hormonalMedicationText,
        contraceptionOther: contraceptionOtherText,
        pastSurgeries: ensureOtherSelected(leftBox, leftBoxOther),
        surgeryOther: leftBoxOther,
        currentMedications: rightBoxText,
      };

      // Use batched update for medication history
      await batchUpdateUser(update, loggedInUser);

      setAdditionalEditing(false);
      setAdditionalFieldErrors({});
      showBanner("Medication history updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update medication history.", "error");
    }
  };

  const handleClinicalSave = async () => {
    try {
      if (!validateEmergencyContact()) return;

      const loggedInUser = getStoredUser();
      if (!loggedInUser?.uid) return;

      const update = {
        emergencyContactName: emergencyName,
        emergencyContactNumber: emergencyContact,
        emergencyContactRelationship: emergencyRelationship,
        emergencyContactRelationshipOther: emergencyRelationshipOther,
        emergencyContact: {
          name: emergencyName,
          relationship: emergencyRelationship === "Other" ? emergencyRelationshipOther : emergencyRelationship,
          contact: emergencyContact,
        },
      };

      // Use immediate save for emergency contact (critical update)
      await immediateUpdateUser(update, loggedInUser);

      setClinicalEditing(false);
      showBanner("Emergency contact updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showBanner("Failed to update emergency contact.", "error");
    }
  };
  
  // ---------------- VALIDATION FOR EMERGENCY CONTACT ----------------
  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case "name":
        if (!value.trim()) newErrors.name = "Name is required";
        else delete newErrors.name;
        break;

      case "relationship":
        if (!value.trim()) newErrors.relationship = "Relationship is required";
        else delete newErrors.relationship;
        break;

      case "contact":
        if (!/^\d{10}$/.test(value)) {
          newErrors.contact = "Contact number must be 10 digits";
        } else if (!value.startsWith("9")) {
          newErrors.contact = "Invalid contact number";
        } else {
          delete newErrors.contact;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  // Async uniqueness checks on blur with debouncing and caching
  const handleUsernameBlur = async (value) => {
    // If value hasn't changed from original, clear any "already taken" error
    if (!hasValueChanged('username', value)) {
      setErrors((p) => ({ ...p, username: null }));
      return null;
    }
    
    const basic = validateUsername(value, false);
    if (basic) {
      setErrors((p) => ({ ...p, username: basic }));
      return basic;
    }
    
    // Check cache first
    const cachedResult = getCachedValidation('username', value);
    if (cachedResult !== null) {
      setErrors((p) => ({ ...p, username: cachedResult }));
      return cachedResult;
    }
    
    // Debounce the validation
    await debounceValidation('username', 300);
    
    try {
      const stored = getStoredUser();
      const uid = stored?.uid;
      const q = query(
        collection(db, "users"),
        where("usernameLower", "==", String(value || "").toLowerCase()),
        limit(1)
      );
      const snap = await getDocs(q);
      const result = (!snap.empty && snap.docs[0].id !== uid) ? "Username is taken" : null;
      
      // Cache the result
      setCachedValidation('username', value, result);
      
      setErrors((p) => ({ ...p, username: result }));
      return result;
    } catch (err) {
      console.warn("Username check failed", err);
      return null;
    }
  };

  const handleEmailBlur = async (value) => {
    // If value hasn't changed from original, clear any "already taken" error
    if (!hasValueChanged('email', value)) {
      setErrors((p) => ({ ...p, email: null }));
      return null;
    }

    const basic = validateEmail(value, false);
    if (basic) {
      setErrors((p) => ({ ...p, email: basic }));
      return basic;
    }

    // Check cache first
    const cachedResult = getCachedValidation('email', value);
    if (cachedResult !== null) {
      setErrors((p) => ({ ...p, email: cachedResult }));
      return cachedResult;
    }

    // Debounce the validation
    await debounceValidation('email', 300);

    try {
      const stored = getStoredUser();
      const uid = stored?.uid;

      // Use comprehensive email check from firestoreService
      const emailExists = await userService.checkEmailExists(value);

      // Additional check to make sure it's not the current user
      let result = null;
      if (emailExists) {
        // Firestore optimization: Use cached user data instead of fetching
        const currentUser = sharedUserDataCacheRef.current || await userService.getUser(uid);
        if (currentUser && currentUser.email !== value.toLowerCase()) {
          result = "Email already taken";
        }
      }

      // Cache the result
      setCachedValidation('email', value, result);

      setErrors((p) => ({ ...p, email: result }));
      return result;
    } catch (err) {
      console.warn("Email check failed", err);
      return null;
    }
  };

  const handleContactBlur = async (value) => {
    // If value hasn't changed from original, clear any error
    if (!hasValueChanged('contact', value)) {
      setErrors((p) => ({ ...p, contact: null }));
      return null;
    }

    // Basic validation first
    const basic = validateContact(value, false);
    if (basic) {
      setErrors((p) => ({ ...p, contact: basic }));
      return basic;
    }

    // Check cache first
    const cachedResult = getCachedValidation('contact', value);
    if (cachedResult !== null) {
      setErrors((p) => ({ ...p, contact: cachedResult }));
      return cachedResult;
    }

    // Debounce the validation
    await debounceValidation('contact', 300);

    // Firestore optimization: Consolidated phone uniqueness check to single query
    try {
      const stored = getStoredUser();
      const uid = stored?.uid;

      // Single query for phone uniqueness (check with +63 prefix first, as it's more common)
      const phoneWithPrefix = `+63${value}`;
      const phoneExists = await userService.checkPhoneExists(phoneWithPrefix);

      // Only check the 10-digit format if the +63 format doesn't exist
      let phoneLast10Exists = false;
      if (!phoneExists) {
        phoneLast10Exists = await userService.checkPhoneExists(value);
      }

      let result = null;
      if (phoneExists || phoneLast10Exists) {
        // Firestore optimization: Use cached user data instead of fetching
        const currentUser = sharedUserDataCacheRef.current || await userService.getUser(uid);
        if (currentUser &&
            currentUser.phone !== phoneWithPrefix &&
            currentUser.phoneLast10 !== value) {
          result = "Contact number already taken";
        }
      }

      // Cache the result
      setCachedValidation('contact', value, result);

      setErrors((p) => ({ ...p, contact: result }));
      return result;
    } catch (err) {
      console.warn("Contact check failed", err);
      return null;
    }
  };

  // Check if any field has changed and all validations pass
  const anyPersonalFieldChanged = () => {
    return (
      tempPersonalData.username !== originalPersonalData.username ||
      tempPersonalData.email !== originalPersonalData.email ||
      tempPersonalData.contact !== originalPersonalData.contact ||
      tempPersonalData.region !== originalPersonalData.region ||
      tempPersonalData.province !== originalPersonalData.province ||
      tempPersonalData.city !== originalPersonalData.city ||
      tempPersonalData.barangay !== originalPersonalData.barangay
    );
  };

  // Check if any account field has changed
  const anyAccountFieldChanged = () => {
    return (
      tempPersonalData.email !== originalPersonalData.email ||
      tempPersonalData.contact !== originalPersonalData.contact
    );
  };

  // Check if there are any validation errors (including pending "already taken" errors)
  const hasValidationErrors = () => {
    return Object.values(errors).some(err => err !== null && err !== undefined && err !== "");
  };

  const personalValid =
    anyPersonalFieldChanged() &&
    // Only validate fields that actually changed
    !validateUsername(tempPersonalData.username, true) &&
    !validateEmail(tempPersonalData.email, true) &&
    !validateContact(tempPersonalData.contact, true) &&
    !hasValidationErrors();

  const accountValid =
    anyAccountFieldChanged() &&
    // Only validate fields that actually changed
    !validateEmail(tempPersonalData.email, true) &&
    !validateContact(tempPersonalData.contact, true) &&
    !hasValidationErrors();

  return (
    <div className="account-panel">
      {isLoading && !hasLoaded ? (
        <div className="profile-skeleton-container">
          {/* Header Skeleton */}
          <div className="account-header-skeleton">
            <div className="account-header-left-skeleton">
              <div className="skeleton skeleton-avatar"></div>
              <div className="skeleton skeleton-name"></div>
              <div className="skeleton skeleton-email"></div>
              <div className="skeleton skeleton-meta"></div>
            </div>
            <div className="account-header-right-skeleton">
              <div className="account-stats-vertical-skeleton">
                {[...Array(3)].map((_, index) => (
                  <div className="skeleton skeleton-stat-card" key={index}></div>
                ))}
              </div>
            </div>
          </div>
          {/* Tabs Skeleton */}
          <div className="profile-tabs-skeleton">
            {[...Array(4)].map((_, index) => (
              <div className="skeleton skeleton-tab" key={index}></div>
            ))}
          </div>
          {/* Content Skeleton */}
          <div className="profile-content-skeleton">
            <div className="skeleton skeleton-section"></div>
            <div className="skeleton skeleton-field"></div>
            <div className="skeleton skeleton-field"></div>
            <div className="skeleton skeleton-field"></div>
          </div>
        </div>
      ) : (
        <>
      {/* Enhanced Profile Header */}
      <div className="account-header">
        <div className="account-header-left">
          <div className="account-profile-card">
            <div className="account-avatar-wrapper">
              <div className="account-avatar">
                <img src="/auravit-logoweb-backup.png" alt="Auravit Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              </div>
              <div className="account-status-indicator">
                <div className="status-dot"></div>
                <span>Active</span>
              </div>
            </div>

            <div className="account-info">
              <div className="account-info-left">
                <h1 className="account-full-name">
                  {personalData.firstName
                    ? `Hi, ${personalData.firstName}`
                    : personalData.username ? `Hi, ${personalData.username}` : 'Hi, User'
                  }
                </h1>
                <p className="account-email">{personalData.email}</p>
              </div>
              <div className="account-info-bottom">
                <div className="account-meta">
                  <div className="meta-item">
                    <FiUser className="meta-icon" />
                    <span>Member since {new Date().getFullYear()}</span>
                  </div>
                  <div className="meta-item">
                    <FiShield className="meta-icon" />
                    <span>Verified Account</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="account-header-right">
          {/* Quick Stats */}
          <div className="account-stats-vertical">
            <div className="stat-card" onClick={() => {
              localStorage.setItem('pendingTab', 'records');
              navigate('/user-home');
            }} style={{ cursor: 'pointer' }}>
              <div className="stat-content">
                <div className="stat-label">Health Records</div>
                <div className="stat-number">{healthRecordsCount >= 100 ? '99+' : healthRecordsCount}</div>
              </div>
            </div>
            <div className="stat-card" onClick={() => {
              localStorage.setItem('pendingTab', 'appointment');
              localStorage.setItem('appointmentNav', 'My Appointments');
              navigate('/user-home');
            }} style={{ cursor: 'pointer' }}>
              <div className="stat-content">
                <div className="stat-label">Appointments</div>
                <div className="stat-number">{confirmedAppointmentsCount >= 100 ? '99+' : confirmedAppointmentsCount}</div>
              </div>
            </div>
            <div className="stat-card" onClick={() => {
              localStorage.setItem('pendingTab', 'notifications');
              navigate('/user-home');
            }} style={{ cursor: 'pointer' }}>
              <div className="stat-content">
                <div className="stat-label">Notifications</div>
                <div className="stat-number">{unreadNotificationsCount >= 100 ? '99+' : unreadNotificationsCount}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="account-tabs">
        <button 
          className={`account-tab ${activeTab === "personal" ? "account-tab--active" : ""}`}
          onClick={() => setActiveTab("personal")}
        >
          <FiUser className="account-tab-icon" />
          <span>Personal &amp; Account Info</span>
        </button>
        <button 
          className={`account-tab ${activeTab === "security" ? "account-tab--active" : ""}`}
          onClick={() => setActiveTab("security")}
        >
          <FiLock className="account-tab-icon" />
          <span>Password &amp; Security</span>
        </button>
        <button 
          className={`account-tab ${activeTab === "health" ? "account-tab--active" : ""}`}
          onClick={() => setActiveTab("health")}
        >
          <FiHeart className="account-tab-icon" />
          <span>Health Info</span>
        </button>
        {!isAssistedAccount && (
          <button 
            className={`account-tab ${activeTab === "emergency" ? "account-tab--active" : ""}`}
            onClick={() => setActiveTab("emergency")}
          >
            <FiShield className="account-tab-icon" />
            <span>Emergency Contact</span>
          </button>
        )}
      </div>

      {/* Tab 1: Personal & Account Info - New grid layout */}
      <div className={`account-tab-content ${activeTab === "personal" ? "" : "hidden"}`}>
        <div className="psp-main" style={{ marginTop: '0' }}>
          {/* Conditional layout: full width Personal Info for assisted users, then two columns for Account and Assisted Account */}
          {personalData.assistanceRequired === "yes" ? (
            <>
              {/* Full width Personal Information section */}
              <div style={{ marginBottom: '16px' }}>
                <section className="psp-section" style={{ padding: '16px' }}>
                  <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                    <h2 className="psp-section-title">{t.personalInformation}</h2>
                    {!personalEdit && (
                      <button 
                        type="button" 
                        className="psp-btn-edit" 
                        onClick={() => setPersonalEdit(true)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div className="psp-field">
                      <label style={{ marginBottom: '6px' }}>{t.dateOfBirth}</label>
                      <input type="date" value={personalData.dob || ""} readOnly />
                    </div>
                    
                    <div className="psp-field">
                      <label style={{ marginBottom: '6px' }}>{t.sexAssignedAtBirth}</label>
                      <input type="text" value={personalData.sex || ""} readOnly />
                    </div>
                  </div>
                  
                  <div className="psp-field" style={{ marginBottom: '10px' }}>
                    <label style={{ marginBottom: '6px' }}>{t.address}</label>
                    {!personalEdit ? (
                      <input
                        type="text"
                        value={personalData.address || ""}
                        readOnly
                        placeholder="e.g., Block 5, Lot 12, Marigold Street, Barangay San Pedro, Quezon City, Metro Manila, National Capital Region"
                      />
                    ) : (
                      <div className="location-group">
                        <div className="form-row">
                          <ScrollableDropdown
                            label="Region"
                            id="profile-region"
                            options={regions}
                            value={selectedRegionId}
                            onChange={(val) => {
                              handlePersonalInputChange("region", val);
                              setErrors((p) => ({ ...p, region: null }));
                            }}
                            placeholder="Select Region"
                            disabled={isRegionsLoading}
                            loading={isRegionsLoading}
                          />
                          {errors.region && <div className="admin-field-error">{errors.region}</div>}

                          <ScrollableDropdown
                            label="Province"
                            id="profile-province"
                            options={provinces}
                            value={selectedProvinceId}
                            onChange={(val) => {
                              handlePersonalInputChange("province", val);
                              setErrors((p) => ({ ...p, province: null }));
                            }}
                            placeholder="Select Province"
                            disabled={isProvincesLoading || !selectedRegionId}
                            loading={isProvincesLoading}
                          />
                          {errors.province && <div className="admin-field-error">{errors.province}</div>}
                        </div>

                        <div className="form-row">
                          <ScrollableDropdown
                            label="City/Municipality"
                            id="profile-city"
                            options={cities}
                            value={selectedCityId}
                            onChange={(val) => {
                              handlePersonalInputChange("city", val);
                              setErrors((p) => ({ ...p, city: null }));
                            }}
                            placeholder="Select City"
                            disabled={isCitiesLoading || !selectedProvinceId}
                            loading={isCitiesLoading}
                          />
                          {errors.city && <div className="admin-field-error">{errors.city}</div>}

                          <ScrollableDropdown
                            label="Barangay"
                            id="profile-barangay"
                            options={barangays}
                            value={selectedBarangayId}
                            onChange={(val) => {
                              handlePersonalInputChange("barangay", val);
                              setErrors((p) => ({ ...p, barangay: null }));
                            }}
                            placeholder="Select Barangay"
                            disabled={isBarangaysLoading || !selectedCityId}
                            loading={isBarangaysLoading}
                          />
                          {errors.barangay && <div className="admin-field-error">{errors.barangay}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {personalEdit && (
                    <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                      <button
                        className="psp-btn-outline"
                        onClick={handlePersonalCancel}
                        disabled={isSaving}
                      >
                        {t.cancel}
                      </button>
                      <button
                        type="button"
                        className="psp-btn-primary"
                        disabled={!personalValid || hasValidationErrors() || isSaving}
                        onClick={handlePersonalSave}
                      >
                        {isSaving ? t.updating : t.updateChanges}
                      </button>
                    </div>
                  )}
                </section>
              </div>

              {/* Two columns: Account Info and Assisted Account Info */}
              <div className="psp-grid">
                <div className="psp-left">
                  {/* Account Information box */}
                  <div className="psp-box" style={{ padding: '16px' }}>
                    <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                      <h2 className="psp-section-title">Account Information</h2>
                      {!accountEdit && (
                        <button 
                          type="button" 
                          className="psp-btn-edit" 
                          onClick={() => setAccountEdit(true)}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    
                    <div className="psp-field" style={{ marginBottom: '10px' }}>
                      <label style={{ marginBottom: '6px' }}>Unique Identifier Code</label>
                      <input type="text" value={personalData.uniqueIdentifierCode || "Not available"} readOnly />
                    </div>
                    
                    <div className="psp-field" style={{ marginBottom: '10px' }}>
                      <label style={{ marginBottom: '6px' }}>Email</label>
                      <input
                        type="email"
                        value={tempPersonalData.email}
                        maxLength={254}
                        readOnly={!accountEdit}
                        onChange={(e) =>
                          handleAccountInputChange("email", e.target.value)
                        }
                        onBlur={(e) => handleEmailBlur(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === " ") e.preventDefault();
                        }}
                        className={errors.email ? "input-error" : ""}
                      />
                      {errors.email && (
                        <div className="admin-field-error">
                          {errors.email}
                        </div>
                      )}
                    </div>
                    
                    <div className="psp-field" style={{ marginBottom: '10px' }}>
                      <label style={{ marginBottom: '6px' }}>Contact Number</label>
                      {accountEdit ? (
                        <div className={`admin-contact-wrapper ${errors.contact ? "input-error" : ""}`} style={{ border: 'none', padding: 0 }}>
                          <span className="admin-country-code">+63</span>
                          <input
                            type="tel"
                            value={tempPersonalData.contact || ""}
                            maxLength={10}
                            onChange={(e) =>
                              handleAccountInputChange("contact", e.target.value)
                            }
                            onBlur={(e) => handleContactBlur(e.target.value)}
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={tempPersonalData.contact ? `+63 ${tempPersonalData.contact}` : ""}
                          readOnly
                        />
                      )}
                      {errors.contact && (
                        <div className="admin-field-error" style={{ textAlign: "left" }}>
                          {errors.contact}
                        </div>
                      )}
                    </div>
                    
                    {accountEdit && (
                      <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                        <button
                          className="psp-btn-outline"
                          onClick={handleAccountCancel}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="psp-btn-primary"
                          disabled={!accountValid || hasValidationErrors() || isSaving}
                          onClick={handleAccountSave}
                        >
                          {isSaving ? "Updating..." : "Save Changes"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="psp-right">
                  {/* Assisted Account Section */}
                  <div className="psp-box" style={{ padding: '16px' }}>
                    <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <h2 className="psp-section-title">Assisted Account</h2>
                        <span style={{ fontSize: '0.875rem', color: '#ffffff', fontWeight: '400' }}>
                          Reason for assistance: {(() => {
                            const reasons = personalData.reasonForAssistance || [];
                            const labels = [];
                            if (reasons.includes('minor')) labels.push("Minor");
                            if (reasons.includes('pwd')) labels.push("PWD");
                            if (reasons.includes('nrnw')) labels.push("NRNW");
                            if (reasons.includes('senior')) labels.push("Senior/Elderly");
                            if (reasons.includes('others')) {
                              const text = personalData.reasonText || "";
                              labels.push(text ? `Other (${text})` : "Other");
                            }
                            return labels.join(", ");
                          })()}
                        </span>
                        {(personalData.disabilityText || personalData.reasonText) && (
                          <span style={{ fontSize: '0.875rem', color: '#ffffff', fontWeight: '400', textAlign: 'left' }}>
                            {personalData.disabilityText && personalData.reasonText 
                              ? `${personalData.disabilityText}, ${personalData.reasonText}`
                              : personalData.disabilityText || personalData.reasonText}
                          </span>
                        )}
                      </div>
                      {!assistedEdit && (
                        <button 
                          type="button" 
                          className="psp-btn-edit" 
                          onClick={handleAssistedEdit}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                        
                    <div style={{ 
                      marginBottom: '12px', 
                      padding: '10px 14px', 
                      background: '#f8fafc', 
                      borderRadius: '10px', 
                      border: '1px solid #e2e8f0' 
                    }}>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '0.875rem', 
                        color: '#475569', 
                        fontWeight: 500 
                      }}>
                        {personalData.reasonForAssistance?.includes('minor') 
                          ? "Guardian contact details for minor account" 
                          : "Accompanist contact details for assisted account"}
                      </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                      <div className="psp-field" style={{ marginBottom: '0' }}>
                        <label style={{ marginBottom: '6px' }}>Full Name</label>
                        <input
                          type="text"
                          value={tempAssistedData.fullName}
                          readOnly={!assistedEdit}
                          onChange={(e) => handleAssistedInputChange("fullName", e.target.value)}
                          placeholder="Enter full name"
                        />
                      </div>

                      <div className="psp-field" style={{ marginBottom: '0' }}>
                        <label style={{ marginBottom: '6px' }}>Relationship</label>
                        {assistedEdit ? (
                          <>
                            <select
                              value={isAssistedOtherTyping ? "Other" : tempAssistedData.relationship}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "Other") {
                                  setIsAssistedOtherTyping(true);
                                  if (RELATIONSHIP_OPTIONS.includes(tempAssistedData.relationship)) {
                                    setTempAssistedData(prev => ({ ...prev, relationship: "" }));
                                  }
                                } else {
                                  setIsAssistedOtherTyping(false);
                                  setTempAssistedData(prev => ({ ...prev, relationship: value }));
                                }
                              }}
                              style={{ 
                                width: '100%', 
                                padding: '12px 16px', 
                                borderRadius: '12px', 
                                border: '2px solid #e2e8f0',
                                fontSize: '14px',
                                backgroundColor: '#fff',
                                cursor: assistedEdit ? 'pointer' : 'not-allowed'
                              }}
                            >
                              <option value="" disabled hidden>Select Relationship</option>
                              {RELATIONSHIP_OPTIONS.map((rel) => (
                                <option key={rel} value={rel}>{rel}</option>
                              ))}
                              <option value="Other">Other</option>
                            </select>
                            {isAssistedOtherTyping && (
                              <input
                                type="text"
                                placeholder="Specify Relationship"
                                value={tempAssistedData.relationship}
                                onChange={(e) => setTempAssistedData(prev => ({ ...prev, relationship: e.target.value }))}
                                style={{ marginTop: '10px' }}
                              />
                            )}
                          </>
                        ) : (
                          <input
                            type="text"
                            value={tempAssistedData.relationship}
                            readOnly
                            placeholder="Enter relationship"
                          />
                        )}
                      </div>

                      <div className="psp-field" style={{ marginBottom: '0' }}>
                        <label style={{ marginBottom: '6px' }}>Contact Number</label>
                        {assistedEdit ? (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            border: '2px solid #e2e8f0', 
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: '#fff'
                          }}>
                            <span style={{ 
                              padding: '12px 16px', 
                              background: '#f8fafc', 
                              color: '#475569', 
                              fontWeight: 600, 
                              fontSize: '14px', 
                              borderRight: '2px solid #e2e8f0' 
                            }}>
                              +63
                            </span>
                            <input
                              type="tel"
                              value={tempAssistedData.contact}
                              readOnly={!assistedEdit}
                              maxLength={10}
                              onChange={(e) => handleAssistedInputChange("contact", e.target.value)}
                              placeholder="9123456789"
                              style={{ 
                                border: 'none', 
                                borderRadius: 0, 
                                flex: 1, 
                                padding: '12px 16px',
                                width: '100%'
                              }}
                            />
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={tempAssistedData.contact ? `+63 ${tempAssistedData.contact}` : ""}
                            readOnly
                          />
                        )}
                        {errors.assistedContact && (
                          <div className="admin-field-error" style={{ textAlign: "left", marginTop: '6px' }}>
                            {errors.assistedContact}
                          </div>
                        )}
                      </div>

                      <div className="psp-field" style={{ marginBottom: '0' }}>
                        <label style={{ marginBottom: '6px' }}>
                          {personalData.reasonForAssistance?.includes('minor') ? "Guardian Address" : "Accompanist Address"}
                        </label>
                        <input
                          type="text"
                          value={tempAssistedData.address}
                          readOnly={!assistedEdit}
                          onChange={(e) => handleAssistedInputChange("address", e.target.value)}
                          placeholder="Enter address"
                        />
                      </div>
                      
                    </div>

                    {assistedEdit && (
                      <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                        <button
                          className="psp-btn-outline"
                          onClick={handleAssistedCancel}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="psp-btn-primary"
                          disabled={isSaving || !!errors.assistedContact}
                          onClick={handleAssistedSave}
                        >
                          {isSaving ? "Updating..." : "Save Changes"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Original layout for non-assisted users
            <div className="psp-grid">
              {/* Left column */}
              <div className="psp-left">
                {/* Personal Information */}
                <section className="psp-section" style={{ padding: '16px' }}>
                  <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                    <h2 className="psp-section-title">{t.personalInformation}</h2>
                    {!personalEdit && (
                      <button 
                        type="button" 
                        className="psp-btn-edit" 
                        onClick={() => setPersonalEdit(true)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div className="psp-field">
                      <label style={{ marginBottom: '6px' }}>{t.dateOfBirth}</label>
                      <input type="date" value={personalData.dob || ""} readOnly />
                    </div>
                    
                    <div className="psp-field">
                      <label style={{ marginBottom: '6px' }}>{t.sexAssignedAtBirth}</label>
                      <input type="text" value={personalData.sex || ""} readOnly />
                    </div>
                  </div>
                  
                  <div className="psp-field" style={{ marginBottom: '10px' }}>
                    <label style={{ marginBottom: '6px' }}>{t.address}</label>
                    {!personalEdit ? (
                      <input
                        type="text"
                        value={personalData.address || ""}
                        readOnly
                        placeholder="e.g., Block 5, Lot 12, Marigold Street, Barangay San Pedro, Quezon City, Metro Manila, National Capital Region"
                      />
                    ) : (
                      <div className="location-group">
                        <div className="form-row">
                          <ScrollableDropdown
                            label="Region"
                            id="profile-region"
                            options={regions}
                            value={selectedRegionId}
                            onChange={(val) => {
                              handlePersonalInputChange("region", val);
                              setErrors((p) => ({ ...p, region: null }));
                            }}
                            placeholder="Select Region"
                            disabled={isRegionsLoading}
                            loading={isRegionsLoading}
                          />
                          {errors.region && <div className="admin-field-error">{errors.region}</div>}

                          <ScrollableDropdown
                            label="Province"
                            id="profile-province"
                            options={provinces}
                            value={selectedProvinceId}
                            onChange={(val) => {
                              handlePersonalInputChange("province", val);
                              setErrors((p) => ({ ...p, province: null }));
                            }}
                            placeholder="Select Province"
                            disabled={isProvincesLoading || !selectedRegionId}
                            loading={isProvincesLoading}
                          />
                          {errors.province && <div className="admin-field-error">{errors.province}</div>}
                        </div>

                        <div className="form-row">
                          <ScrollableDropdown
                            label="City/Municipality"
                            id="profile-city"
                            options={cities}
                            value={selectedCityId}
                            onChange={(val) => {
                              handlePersonalInputChange("city", val);
                              setErrors((p) => ({ ...p, city: null }));
                            }}
                            placeholder="Select City"
                            disabled={isCitiesLoading || !selectedProvinceId}
                            loading={isCitiesLoading}
                          />
                          {errors.city && <div className="admin-field-error">{errors.city}</div>}

                          <ScrollableDropdown
                            label="Barangay"
                            id="profile-barangay"
                            options={barangays}
                            value={selectedBarangayId}
                            onChange={(val) => {
                              handlePersonalInputChange("barangay", val);
                              setErrors((p) => ({ ...p, barangay: null }));
                            }}
                            placeholder="Select Barangay"
                            disabled={isBarangaysLoading || !selectedCityId}
                            loading={isBarangaysLoading}
                          />
                          {errors.barangay && <div className="admin-field-error">{errors.barangay}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {personalEdit && (
                    <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                      <button
                        className="psp-btn-outline"
                        onClick={handlePersonalCancel}
                        disabled={isSaving}
                      >
                        {t.cancel}
                      </button>
                      <button
                        type="button"
                        className="psp-btn-primary"
                        disabled={!personalValid || hasValidationErrors() || isSaving}
                        onClick={handlePersonalSave}
                      >
                        {isSaving ? t.updating : t.updateChanges}
                      </button>
                    </div>
                  )}
                </section>
              </div>

              {/* Right column */}
              <div className="psp-right">
                {/* Account Information box */}
                <div className="psp-box" style={{ padding: '16px' }}>
                  <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                    <h2 className="psp-section-title">Account Information</h2>
                    {!accountEdit && (
                      <button 
                        type="button" 
                        className="psp-btn-edit" 
                        onClick={() => setAccountEdit(true)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  
                  <div className="psp-field" style={{ marginBottom: '10px' }}>
                    <label style={{ marginBottom: '6px' }}>Unique Identifier Code</label>
                    <input type="text" value={personalData.uniqueIdentifierCode || "Not available"} readOnly />
                  </div>
                  
                  <div className="psp-field" style={{ marginBottom: '10px' }}>
                    <label style={{ marginBottom: '6px' }}>Email</label>
                    <input
                      type="email"
                      value={tempPersonalData.email}
                      maxLength={254}
                      readOnly={!accountEdit}
                      onChange={(e) =>
                        handleAccountInputChange("email", e.target.value)
                      }
                      onBlur={(e) => handleEmailBlur(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === " ") e.preventDefault();
                      }}
                      className={errors.email ? "input-error" : ""}
                    />
                    {errors.email && (
                      <div className="admin-field-error">
                        {errors.email}
                      </div>
                    )}
                  </div>
                  
                  <div className="psp-field" style={{ marginBottom: '10px' }}>
                    <label style={{ marginBottom: '6px' }}>Contact Number</label>
                    {accountEdit ? (
                      <div className={`admin-contact-wrapper ${errors.contact ? "input-error" : ""}`} style={{ border: 'none', padding: 0 }}>
                        <span className="admin-country-code">+63</span>
                        <input
                          type="tel"
                          value={tempPersonalData.contact || ""}
                          maxLength={10}
                          onChange={(e) =>
                            handleAccountInputChange("contact", e.target.value)
                          }
                          onBlur={(e) => handleContactBlur(e.target.value)}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={tempPersonalData.contact ? `+63 ${tempPersonalData.contact}` : ""}
                        readOnly
                      />
                    )}
                    {errors.contact && (
                      <div className="admin-field-error" style={{ textAlign: "left" }}>
                        {errors.contact}
                      </div>
                    )}
                  </div>
                  
                  {accountEdit && (
                    <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                      <button
                        className="psp-btn-outline"
                        onClick={handleAccountCancel}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="psp-btn-primary"
                        disabled={!accountValid || hasValidationErrors() || isSaving}
                        onClick={handleAccountSave}
                      >
                        {isSaving ? "Updating..." : "Save Changes"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab 2: Security */}
      <div className={`account-tab-content ${activeTab === "security" ? "" : "hidden"}`}>
        <div className="psp-main" style={{ marginTop: '0' }}>
          <div className="psp-grid">
            <div className="psp-left">
              {/* Change Password */}
              <section className="psp-section" style={{ padding: '16px' }}>
                <UserChangePassword 
                  userData={personalData} 
                  onPasswordOtpRequired={(data) => {
                    setPasswordChangeData(data);
                    setUserChangeType('password');
                    setShowUserChangeOtpModal(true);
                  }}
                />
              </section>
            </div>
            <div className="psp-right">
              {/* Access & Security box */}
              <div className="psp-box" style={{ padding: '16px' }}>
                <div className="psp-section-header" style={{ marginBottom: '20px' }}>
                  <h2 className="psp-section-title">Access &amp; Security</h2>
                </div>
                <div className="psp-toggle-row" style={{ marginBottom: '12px' }}>
                  <div>
                    <div className="psp-toggle-label">Two-Factor Authentication</div>
                    <div className="psp-toggle-desc" style={{ textAlign: 'left' }}>Manage how your account is verified during login using one-time password (OTP) authentication.</div>
                  </div>
                  <button
                    type="button"
                    className={`psp-toggle ${twoFactorEnabled ? "psp-toggle-on" : ""}`}
                    onClick={() => updateTwoFactor(!twoFactorEnabled)}
                    aria-label="Toggle two-factor authentication"
                  >
                    <span className="psp-toggle-knob" />
                  </button>
                </div>
                
                {/* Account Information */}
                <div className="psp-account-info" style={{ marginBottom: '12px' }}>
                  <div className="psp-account-info-header">
                    <h3 className="psp-account-info-title">Account Information</h3>
                  </div>
                  <div className="psp-row-fields">
                    <div className="psp-field" style={{ marginBottom: '0' }}>
                      <label style={{ marginBottom: '6px' }}>Date Created</label>
                      <div className="psp-field-value" style={{ lineHeight: '1.5' }}>
                        <div>{parseFirebaseTimestamp(createdAt)?.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) || 'N/A'}</div>
                        <div style={{ fontSize: '0.85em', color: '#64748b' }}>{parseFirebaseTimestamp(createdAt)?.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase() || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="psp-field" style={{ marginBottom: '0' }}>
                      <label style={{ marginBottom: '6px' }}>Last Login</label>
                      <div className="psp-field-value" style={{ lineHeight: '1.5' }}>
                        <div>{lastLogin ? parseFirebaseTimestamp(lastLogin)?.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : 'First Login'}</div>
                        <div style={{ fontSize: '0.85em', color: '#64748b' }}>{lastLogin ? parseFirebaseTimestamp(lastLogin)?.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase() : ''}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="psp-sessions">
                  <div className="psp-sessions-header">
                    <div className="psp-sessions-label">Where You're Logged In</div>
                    <button
                      type="button"
                      className="psp-btn-revoke psp-btn-logout-all"
                      style={{ backgroundColor: 'transparent', borderColor: '#dc2626', color: '#dc2626' }}
                      onClick={handleLogoutAllClick}
                      disabled={logoutAllLoading}
                    >
                      {logoutAllLoading ? "Signing out…" : "Log Out All Sessions"}
                    </button>
                  </div>

                  {/* Logout-all confirmation */}
                  {showLogoutAllConfirm && (
                    <div className="psp-session-confirm-overlay" role="dialog" aria-modal="true" aria-label="Confirm log out all sessions">
                      <div className="psp-session-confirm-box">
                        <p>This will sign you out of <strong>all devices</strong>. Continue?</p>
                        <div className="psp-session-confirm-actions" style={{ justifyContent: 'center' }}>
                          <button
                            type="button"
                            className="psp-btn-revoke"
                            style={{ borderColor: '#2563eb', color: '#2563eb', backgroundColor: 'transparent' }}
                            onClick={() => setShowLogoutAllConfirm(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="psp-btn-revoke"
                            style={{ borderColor: '#dc2626', color: '#dc2626', backgroundColor: 'transparent' }}
                            onClick={handleLogoutAllConfirm}
                          >
                            Yes, log out all
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {sessionsLoading ? (
                    <p className="psp-sessions-loading" style={{ fontSize: '0.85rem', color: '#64748b', padding: '8px 0' }}>
                      Loading sessions…
                    </p>
                  ) : activeSessions.length === 0 ? (
                    <p className="psp-sessions-empty" style={{ fontSize: '0.85rem', color: '#64748b', padding: '8px 0' }}>
                      No active sessions found.
                    </p>
                  ) : (
                    <ul className="psp-session-list" style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
                      {activeSessions.map((s) => {
                        const isCurrent = s.sessionId === currentSessionId;
                        const isRevoking = revokeLoadingId === s.sessionId;
                        const lastActive = s.lastActiveAt?.toDate?.() ?? null;
                        const lastActiveLabel = lastActive
                          ? formatSessionTime(lastActive)
                          : "Unknown";
                        return (
                          <li key={s.sessionId} className={`psp-session-item${isCurrent ? " psp-session-item--current" : ""}`}>
                            <div className="psp-session-info">
                              <span className="psp-session-device">
                                {s.deviceName || `${s.browser || "Unknown"} on ${s.operatingSystem || "Unknown"}`}
                                {isCurrent && (
                                  <span className="psp-session-badge-current" style={{
                                    marginLeft: '8px',
                                    fontSize: '0.72rem',
                                    background: '#dcfce7',
                                    color: '#15803d',
                                    borderRadius: '4px',
                                    padding: '1px 6px',
                                    fontWeight: 600,
                                  }}>
                                    Current session
                                  </span>
                                )}
                              </span>
                              <span className="psp-session-location" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                {s.deviceType || "Desktop"} · Last active: {lastActiveLabel}
                              </span>
                            </div>
                            {!isCurrent && (
                              <button
                                type="button"
                                className="psp-btn-revoke"
                                onClick={() => handleRevokeSession(s.sessionId)}
                                disabled={isRevoking || logoutAllLoading}
                                aria-label={`Revoke session on ${s.deviceName}`}
                              >
                                {isRevoking ? "Revoking…" : "Revoke"}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`account-tab-content ${activeTab === "health" ? "" : "hidden"}`}>
        <div className="psp-main" style={{ marginTop: '0.5rem' }}>
          {/* Top Section: Health & Lifestyle (Full Width) */}
          <section className="psp-section" style={{ padding: '16px', marginBottom: '16px' }}>
            <div className="psp-section-header" style={{ marginBottom: '12px' }}>
              <h2 className="psp-section-title">Health & Lifestyle</h2>
              {!healthEditing && (
                <button 
                  type="button" 
                  className="psp-btn-edit" 
                  onClick={handleHealthEdit}
                >
                  Edit
                </button>
              )}
            </div>
            
            <div className="psp-field" style={{ marginBottom: '10px' }}>
              <label style={{ marginBottom: '6px' }}>Health / Medical History</label>
              {(() => {
                const hasSelection = medicalHistory.length > 0;
                if (!healthEditing && !hasSelection) {
                  return <p className="no-information-text">No Information</p>;
                }
                return (
                  <>
                    <div className="hh-chip-group">
                      {(healthEditing ? CONDITIONS : CONDITIONS.filter(item => medicalHistory.includes(item))).map((item) => (
                        <button
                          key={item}
                          className={`hh-chip ${medicalHistory.includes(item) ? "hh-chip--selected" : ""} ${!healthEditing && !medicalHistory.includes(item) ? "hh-chip--disabled" : ""}`}
                          disabled={!healthEditing && !medicalHistory.includes(item)}
                          onClick={() => toggleItem(item, medicalHistory, setMedicalHistory, setMedicalOther)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                    {medicalHistory.includes("Others (Please Specify)") && (
                      <div className="hh-char-counter-wrapper">
                        <textarea 
                          type="text" 
                          className={`hh-other-input ${healthFieldErrors.medicalOther ? 'hh-other-input--error' : ''}`}
                          placeholder="Please specify" 
                          disabled={!healthEditing} 
                          maxLength={150}
                          rows={3}
                          value={medicalOther} 
                          onChange={(e) => {
                            setMedicalOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                            if (healthFieldErrors.medicalOther) {
                              setHealthFieldErrors(prev => ({ ...prev, medicalOther: null }));
                            }
                          }} 
                        />
                        {healthEditing && <span className="hh-char-counter">{medicalOther.length}/150</span>}
                        {healthEditing && healthFieldErrors.medicalOther && (
                          <span className="hh-field-error">{healthFieldErrors.medicalOther}</span>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="psp-field" style={{ marginBottom: '10px' }}>
              <label style={{ marginBottom: '6px' }}>Medication Allergies</label>
              {(() => {
                const hasSelection = allergies.length > 0;
                if (!healthEditing && !hasSelection) {
                  return <p className="no-information-text">No Information</p>;
                }
                return (
                  <>
                    <div className="hh-chip-group">
                      {(healthEditing ? ALLERGIES.filter(item => userSex !== "male" || item !== "Hormonal Medications (Pills, Patches, Injections)") : ALLERGIES.filter(item => allergies.includes(item) && (userSex !== "male" || item !== "Hormonal Medications (Pills, Patches, Injections)"))).map((item) => (
                        <button
                          key={item}
                          className={`hh-chip ${allergies.includes(item) ? "hh-chip--selected" : ""} ${!healthEditing && !allergies.includes(item) ? "hh-chip--disabled" : ""}`}
                          disabled={!healthEditing && !allergies.includes(item)}
                          onClick={() =>
                            toggleItem(
                              item,
                              allergies,
                              setAllergies,
                              item === "Others (Please Specify)"
                                ? setAllergyOther
                                : item === "Hormonal Medications (Pills, Patches, Injections)"
                                  ? setHormonalMedicationText
                                  : undefined
                            )
                          }
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                    {userSex !== "male" && allergies.includes("Hormonal Medications (Pills, Patches, Injections)") && (
                      <div className="hh-char-counter-wrapper">
                        <textarea 
                          type="text" 
                          className={`hh-other-input ${healthFieldErrors.hormonalMedicationText ? 'hh-other-input--error' : ''}`}
                          placeholder="Please specify hormonal medications" 
                          disabled={!healthEditing} 
                          maxLength={150}
                          rows={3}
                          value={hormonalMedicationText} 
                          onChange={(e) => {
                            setHormonalMedicationText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                            if (healthFieldErrors.hormonalMedicationText) {
                              setHealthFieldErrors(prev => ({ ...prev, hormonalMedicationText: null }));
                            }
                          }} 
                        />
                        {healthEditing && <span className="hh-char-counter">{hormonalMedicationText.length}/150</span>}
                        {healthEditing && healthFieldErrors.hormonalMedicationText && (
                          <span className="hh-field-error">{healthFieldErrors.hormonalMedicationText}</span>
                        )}
                      </div>
                    )}

                    {allergies.includes("Others (Please Specify)") && (
                      <div className="hh-char-counter-wrapper">
                        <textarea 
                          type="text" 
                          className={`hh-other-input ${healthFieldErrors.allergyOther ? 'hh-other-input--error' : ''}`}
                          placeholder="Please specify" 
                          disabled={!healthEditing} 
                          maxLength={150}
                          rows={3}
                          value={allergyOther} 
                          onChange={(e) => {
                            setAllergyOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                            if (healthFieldErrors.allergyOther) {
                              setHealthFieldErrors(prev => ({ ...prev, allergyOther: null }));
                            }
                          }} 
                        />
                        {healthEditing && <span className="hh-char-counter">{allergyOther.length}/150</span>}
                        {healthEditing && healthFieldErrors.allergyOther && (
                          <span className="hh-field-error">{healthFieldErrors.allergyOther}</span>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="psp-field" style={{ marginBottom: '10px' }}>
              <label style={{ marginBottom: '6px' }}>Lifestyle Indicators</label>
              {(() => {
                const hasSelection = alcoholUse || smoking;
                if (!healthEditing && !hasSelection) {
                  return <p className="no-information-text">No Information</p>;
                }
                return (
                  <div className="hh-lifestyle-rows">
                    <div className="hh-lifestyle-row-inline">
                      <img src={alcoholIcon} alt="Alcohol Icon" className="hh-lifestyle-icon" />
                      <span className="hh-lifestyle-label">Alcohol Use</span>
                      <div className="hh-radio-group">
                        {(healthEditing ? ["Regularly", "Occasionally", "Never"] : (alcoholUse ? [alcoholUse] : [])).map((opt) => (
                          <label key={opt} className={`hh-radio-pill ${alcoholUse === opt ? 'hh-radio-pill--checked' : ''}`}>
                            <input type="radio" name="alcohol" disabled={!healthEditing} checked={alcoholUse === opt} onChange={() => setAlcoholUse(opt)} />
                            <div className="hh-custom-radio"></div>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="hh-lifestyle-row-inline">
                      <img src={cigaretteIcon} alt="Cigarette Icon" className="hh-lifestyle-icon" />
                      <span className="hh-lifestyle-label">Smoking</span>
                      <div className="hh-radio-group">
                        {(healthEditing ? ["Regularly", "Occasionally", "Never"] : (smoking ? [smoking] : [])).map((opt) => (
                          <label key={opt} className={`hh-radio-pill ${smoking === opt ? 'hh-radio-pill--checked' : ''}`}>
                            <input type="radio" name="smoking" disabled={!healthEditing} checked={smoking === opt} onChange={() => setSmoking(opt)} />
                            <div className="hh-custom-radio"></div>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {healthEditing && (
              <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                <button
                  className="psp-btn-outline"
                  onClick={handleHealthCancel}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="psp-btn-primary"
                  onClick={handleHealthSave}
                >
                  Save Changes
                </button>
              </div>
            )}
          </section>

          {/* Bottom Section: 2 Columns */}
          <div className="psp-grid">
            {/* Left Column - Medication History */}
            <div className="psp-left">
              <section className="psp-section" style={{ padding: '16px' }}>
                <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                  <h2 className="psp-section-title">Medication History</h2>
                  {!additionalEditing && (
                    <button 
                      type="button" 
                      className="psp-btn-edit" 
                      onClick={handleAdditionalEdit}
                    >
                      Edit
                    </button>
                  )}
                </div>
                
                {userSex !== "male" && (
                  <div className="psp-field" style={{ marginBottom: '10px' }}>
                    <label style={{ marginBottom: '6px' }}>Currently on Hormonal Contraception</label>
                    {(() => {
                      const hasSelection = additionalHistory.length > 0;
                      if (!additionalEditing && !hasSelection) {
                        return <p className="no-information-text">No Information</p>;
                      }
                      return (
                        <>
                          <div className="hh-chip-group">
                            {(additionalEditing ? MEDICATION_HISTORY : MEDICATION_HISTORY.filter(item => additionalHistory.includes(item))).map((item) => (
                              <button
                                key={item}
                                className={`hh-chip ${additionalHistory.includes(item) ? "hh-chip--selected" : ""} ${!additionalEditing && !additionalHistory.includes(item) ? "hh-chip--disabled" : ""}`}
                                disabled={!additionalEditing && !additionalHistory.includes(item)}
                                onClick={() => toggleItem(item, additionalHistory, setAdditionalHistory, setContraceptionOtherText)}
                              >
                                {item}
                              </button>
                            ))}
                          </div>
                          {additionalHistory.includes("Others (Please Specify)") && (
                            <div className="hh-char-counter-wrapper">
                              <textarea
                                type="text"
                                className={`hh-other-input ${additionalFieldErrors.contraceptionOtherText ? 'hh-other-input--error' : ''}`}
                                placeholder="Please specify"
                                disabled={!additionalEditing}
                                maxLength={150}
                                rows={3}
                                value={contraceptionOtherText}
                                onChange={(e) => {
                                  setContraceptionOtherText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                                  if (additionalFieldErrors.contraceptionOtherText) {
                                    setAdditionalFieldErrors(prev => ({ ...prev, contraceptionOtherText: null }));
                                  }
                                }}
                              />
                              {additionalEditing && <span className="hh-char-counter">{contraceptionOtherText.length}/150</span>}
                              {additionalEditing && additionalFieldErrors.contraceptionOtherText && (
                                <span className="hh-field-error">{additionalFieldErrors.contraceptionOtherText}</span>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                <div className="psp-field" style={{ marginBottom: '10px' }}>
                  <label style={{ marginBottom: '6px' }}>Past Surgeries</label>
                  {(() => {
                    const PAST_SURGERIES_LOCAL = [
                      "Abdominal Section",
                      "Pelvic Surgery",
                      ...(userSex === "male" ? ["Prostate Surgery", "Testicular Surgery"] : []),
                      ...(userSex === "female" ? ["Cesarean Section", "Hysterectomy"] : []),
                      "Others (Please Specify)"
                    ];
                    const hasSelection = leftBox.length > 0;
                    if (!additionalEditing && !hasSelection) {
                      return <p className="no-information-text">No Information</p>;
                    }
                    return (
                      <>
                        <div className="hh-chip-group">
                          {(additionalEditing ? PAST_SURGERIES_LOCAL : PAST_SURGERIES_LOCAL.filter(item => leftBox.includes(item))).map((item) => (
                            <button
                              key={item}
                              className={`hh-chip ${leftBox.includes(item) ? "hh-chip--selected" : ""} ${!additionalEditing && !leftBox.includes(item) ? "hh-chip--disabled" : ""}`}
                              disabled={!additionalEditing && !leftBox.includes(item)}
                              onClick={() => toggleItem(item, leftBox, setLeftBox, setLeftBoxOther)}
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                        {leftBox.includes("Others (Please Specify)") && (
                          <div className="hh-char-counter-wrapper">
                            <textarea
                              type="text"
                              className={`hh-other-input ${additionalFieldErrors.leftBoxOther ? 'hh-other-input--error' : ''}`}
                              placeholder="Please specify"
                              disabled={!additionalEditing}
                              maxLength={150}
                              rows={3}
                              value={leftBoxOther}
                              onChange={(e) => {
                                setLeftBoxOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                                if (additionalFieldErrors.leftBoxOther) {
                                  setAdditionalFieldErrors(prev => ({ ...prev, leftBoxOther: null }));
                                }
                              }}
                            />
                            {additionalEditing && <span className="hh-char-counter">{leftBoxOther.length}/150</span>}
                            {additionalEditing && additionalFieldErrors.leftBoxOther && (
                              <span className="hh-field-error">{additionalFieldErrors.leftBoxOther}</span>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="psp-field" style={{ marginBottom: '10px' }}>
                  <label style={{ marginBottom: '6px' }}>Current Medications</label>
                  {(() => {
                    const hasSelection = rightBoxText;
                    if (!additionalEditing && !hasSelection) {
                      return <p className="no-information-text">No Information</p>;
                    }
                    return (
                      <div className="hh-char-counter-wrapper">
                        <textarea
                          className="hh-other-input"
                          rows={5}
                          disabled={!additionalEditing}
                          maxLength={150}
                          value={rightBoxText}
                          onChange={(e) => setRightBoxText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''))}
                        />
                        {additionalEditing && <span className="hh-char-counter">{rightBoxText.length}/150</span>}
                      </div>
                    );
                  })()}
                </div>

                {additionalEditing && (
                  <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="psp-btn-outline"
                      onClick={handleAdditionalCancel}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="psp-btn-primary"
                      onClick={handleAdditionalSave}
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </section>
            </div>

            {/* Right Column - Sexual Health History */}
            <div className="psp-right">
              <section className="psp-section" style={{ padding: '16px' }}>
                <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                  <h2 className="psp-section-title">Sexual Health History</h2>
                  {!hivEditing && (
                    <button 
                      type="button" 
                      className="psp-btn-edit" 
                      onClick={handleHivEdit}
                    >
                      Edit
                    </button>
                  )}
                </div>
                
                <div className="psp-field" style={{ marginBottom: '10px' }}>
                  <label style={{ marginBottom: '6px' }}>History of Sexually Transmitted Infections (STIs)</label>
                  {(() => {
                    const hasSelection = hivSelection.length > 0;
                    if (!hivEditing && !hasSelection) {
                      return <p className="no-information-text">No Information</p>;
                    }
                    return (
                      <>
                        <div className="hh-chip-group">
                          {(hivEditing ? INFECTIONS_OPTIONS : INFECTIONS_OPTIONS.filter(opt => hivSelection.includes(opt))).map((opt) => (
                            <button
                              key={opt}
                              className={`hh-chip ${hivSelection.includes(opt) ? "hh-chip--selected" : ""} ${!hivEditing && !hivSelection.includes(opt) ? "hh-chip--disabled" : ""}`}
                              disabled={!hivEditing && !hivSelection.includes(opt)}
                              onClick={() => {
                                if (hivSelection.includes(opt)) {
                                  setHivSelection(hivSelection.filter(i => i !== opt));
                                  if (opt === "Others (Please Specify)") setStiOther("");
                                } else {
                                  setHivSelection([...hivSelection, opt]);
                                }
                              }}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                        {hivSelection.includes("Others (Please Specify)") && (
                          <div className="hh-char-counter-wrapper">
                            <textarea 
                              type="text" 
                              className={`hh-other-input ${stiFieldErrors.stiOther ? 'hh-other-input--error' : ''}`}
                              placeholder="Please specify" 
                              disabled={!hivEditing} 
                              maxLength={150}
                              rows={3}
                              value={stiOther} 
                              onChange={(e) => {
                                setStiOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                                if (stiFieldErrors.stiOther) {
                                  setStiFieldErrors(prev => ({ ...prev, stiOther: null }));
                                }
                              }} 
                            />
                            {hivEditing && <span className="hh-char-counter">{stiOther.length}/150</span>}
                            {hivEditing && stiFieldErrors.stiOther && (
                              <span className="hh-field-error">{stiFieldErrors.stiOther}</span>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="psp-field" style={{ marginBottom: '10px' }}>
                  <label style={{ marginBottom: '6px' }}>HIV Status</label>
                  {(() => {
                    const hasSelection = hivLeftBox;
                    if (!hivEditing && !hasSelection) {
                      return <p className="no-information-text">No Information</p>;
                    }
                    return (
                      <div className="radio-list">
                        {(hivEditing ? HIV_OPTIONS : (hivLeftBox ? [hivLeftBox] : [])).map((opt) => (
                          <div key={opt} className="hiv-row-container">
                            <label className={`radio-box ${hivLeftBox === opt ? 'radio-box--checked' : ''}`}>
                              <input
                                type="radio"
                                name="hivStatus"
                                disabled={!hivEditing}
                                checked={hivLeftBox === opt}
                                onChange={() => {
                                  setHivLeftBox(opt);
                                  setErrors((prev) => ({ ...prev, hivStatus: "" }));
                                  if (opt === "Unknown") {
                                    setLastTestedDate("");
                                    setErrors((prev) => ({ ...prev, lastTestedDate: "" }));
                                  }
                                }}
                              />
                              <span className="checkmark"></span>
                              <span className="option-text">{opt}</span>
                            </label>
                            {opt !== "Unknown" && hivLeftBox === opt && (
                              <div className="date-input-container">
                                <input
                                  type="date"
                                  className={`styled-date-input ${errors.lastTestedDate ? "input-error" : ""}`}
                                  disabled={!hivEditing}
                                  value={lastTestedDate || ""}
                                  min={personalData.dob}
                                  max={new Date().toISOString().split('T')[0]}
                                  onChange={(e) => {
                                    setLastTestedDate(e.target.value.slice(0, 10));
                                    setErrors((prev) => ({ ...prev, lastTestedDate: "" }));
                                  }}
                                />
                                {errors.lastTestedDate && (
                                  <div className="admin-field-error" style={{ fontSize: '11px' }}>
                                    {errors.lastTestedDate}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                <div className="psp-field" style={{ marginBottom: '10px' }}>
                  <label style={{ marginBottom: '6px' }}>History of Genital Infections / Other Conditions</label>
                  {(() => {
                    const hasSelection = hivRightBoxText;
                    if (!hivEditing && !hasSelection) {
                      return <p className="no-information-text">No Information</p>;
                    }
                    return (
                      <div className="hh-char-counter-wrapper">
                        <textarea 
                          className="hh-other-input" 
                          rows={5} 
                          disabled={!hivEditing} 
                          maxLength={150}
                          value={hivRightBoxText} 
                          onChange={e => setHivRightBoxText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''))} 
                        />
                        {hivEditing && <span className="hh-char-counter">{hivRightBoxText.length}/150</span>}
                      </div>
                    );
                  })()}
                </div>

                {hivEditing && (
                  <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="psp-btn-outline"
                      onClick={handleHivCancel}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="psp-btn-primary"
                      onClick={handleHivSave}
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Tab4: Emergency Contact - Only show for non-assisted accounts */}
      {!isAssistedAccount && (
        <div className={`account-tab-content ${activeTab === "emergency" ? "" : "hidden"}`}>
        <div className="psp-main" style={{ marginTop: '0.5rem' }}>
          <div className="psp-grid">
            <div className="psp-left" style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
              {/* Emergency Contact */}
              <section className="psp-section" style={{ padding: '16px' }}>
                <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                  <h2 className="psp-section-title">Emergency Contact</h2>
                  {!clinicalEditing && (
                    <button 
                      type="button" 
                      className="psp-btn-edit" 
                      onClick={handleClinicalEdit}
                    >
                      Edit
                    </button>
                  )}
                </div>
                
                <div style={{ marginTop: '10px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                    <div className="psp-field" style={{ marginBottom: '0' }}>
                      <label style={{ marginBottom: '6px' }}>Name</label>
                      <input
                        type="text"
                        placeholder="Full Name"
                        disabled={!clinicalEditing}
                        maxLength={50}
                        value={emergencyName}
                        onChange={(e) => {
                          const sanitized = e.target.value.replace(/[^a-zA-Z'\- ]/g, '');
                          setEmergencyName(sanitized.slice(0, 50));
                          validateField("name", sanitized.slice(0, 50));
                        }}
                        className={errors.name ? "input-error" : ""}
                      />
                      {errors.name && (
                        <div className="admin-field-error">{errors.name}</div>
                      )}
                    </div>

                    <div className="psp-field" style={{ marginBottom: '0' }}>
                      <label style={{ marginBottom: '6px' }}>Relationship</label>
                      {clinicalEditing ? (
                        <>
                          <select
                            disabled={!clinicalEditing}
                            value={isOtherTyping ? "Other" : emergencyRelationship}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value === "Other") {
                                setIsOtherTyping(true);
                                if (RELATIONSHIP_OPTIONS.includes(emergencyRelationship)) {
                                  setEmergencyRelationship("Other");
                                }
                              } else {
                                setIsOtherTyping(false);
                                setEmergencyRelationship(value);
                                setEmergencyRelationshipOther("");
                                validateField("relationship", value);
                              }
                            }}
                            style={{ 
                              width: '100%', 
                              padding: '12px 16px', 
                              borderRadius: '12px', 
                              border: '2px solid #e2e8f0',
                              fontSize: '14px',
                              backgroundColor: '#fff',
                              cursor: clinicalEditing ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <option value="" disabled hidden>
                              Select Relationship
                            </option>
                            {RELATIONSHIP_OPTIONS.map((rel) => (
                              <option key={rel} value={rel}>
                                {rel}
                              </option>
                            ))}
                            <option value="Other">Other</option>
                          </select>

                          {isOtherTyping && (
                            <input
                              type="text"
                              placeholder="Specify Relationship"
                              disabled={!clinicalEditing}
                              value={emergencyRelationshipOther}
                              onChange={(e) => {
                                const sanitized = e.target.value.replace(/[^a-zA-Z]/g, '');
                                setEmergencyRelationshipOther(sanitized.slice(0, 20));
                                validateField("relationship", sanitized.slice(0, 20));
                              }}
                              maxLength={20}
                              style={{ marginTop: '8px' }}
                            />
                          )}
                        </>
                      ) : (
                        <input
                          type="text"
                          value={isOtherTyping ? emergencyRelationshipOther : emergencyRelationship}
                          readOnly
                          placeholder="Enter relationship"
                        />
                      )}
                      {errors.relationship && (
                        <div className="admin-field-error">{errors.relationship}</div>
                      )}
                    </div>

                    <div className="psp-field" style={{ marginBottom: '0' }}>
                      <label style={{ marginBottom: '6px' }}>Contact Number</label>
                      {clinicalEditing ? (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          border: `2px solid ${errors.contact ? '#dc2626' : '#e2e8f0'}`, 
                          borderRadius: '12px',
                          overflow: 'hidden',
                          background: '#fff'
                        }}>
                          <span style={{ 
                            padding: '12px 16px', 
                            background: '#f8fafc', 
                            color: '#475569', 
                            fontWeight: 600, 
                            fontSize: '14px', 
                            borderRight: '2px solid #e2e8f0' 
                          }}>
                            +63
                          </span>
                          <input
                            type="text"
                            placeholder="912345678"
                            disabled={!clinicalEditing}
                            value={emergencyContact}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, "");
                              setEmergencyContact(digits);
                              validateField("contact", digits);
                            }}
                            maxLength={10}
                            style={{ 
                              border: 'none', 
                              borderRadius: 0, 
                              flex: 1, 
                              padding: '12px 16px',
                              width: '100%'
                            }}
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={emergencyContact ? `+63 ${emergencyContact}` : ""}
                          readOnly
                        />
                      )}
                      {errors.contact && (
                        <div className="admin-field-error" style={{ textAlign: "left" }}>
                          {errors.contact}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {clinicalEditing && (
                  <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="psp-btn-outline"
                      onClick={handleClinicalCancel}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="psp-btn-primary"
                      onClick={handleClinicalSave}
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Original grid layout to be hidden when tabs are active */}
      <div className="hidden">
        {/* New grid layout - Left and Right columns */}
      <div className="psp-main" style={{ marginTop: '0' }}>
        <div className="psp-grid">
          {/* Left column */}
          <div className="psp-left">
            {/* Personal Information */}
            <section className="psp-section" style={{ padding: '16px' }}>
              <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                <h2 className="psp-section-title">{t.personalInformation}</h2>
                {!personalEdit && (
                  <button 
                    type="button" 
                    className="psp-btn-edit" 
                    onClick={() => setPersonalEdit(true)}
                  >
                    Edit
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div className="psp-field">
                  <label style={{ marginBottom: '6px' }}>{t.dateOfBirth}</label>
                  <input type="date" value={personalData.dob || ""} readOnly />
                </div>
                <div className="psp-field">
                  <label style={{ marginBottom: '6px' }}>{t.sexAssignedAtBirth}</label>
                  <input type="text" value={personalData.sex || ""} readOnly />
                </div>
              </div>

              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>{t.address}</label>
                {!personalEdit ? (
                  <input
                    type="text"
                    value={personalData.address || ""}
                    readOnly
                    placeholder="e.g., Block 5, Lot 12, Marigold Street, Barangay San Pedro, Quezon City, Metro Manila, National Capital Region"
                  />
                ) : (
                  <div className="location-group">
                    <div className="form-row">
                      <ScrollableDropdown
                        label="Region"
                        id="profile-region"
                        options={regions}
                        value={selectedRegionId}
                        onChange={(val) => {
                          handlePersonalInputChange("region", val);
                          setErrors((p) => ({ ...p, region: null }));
                        }}
                        placeholder="Select Region"
                        disabled={isRegionsLoading}
                        loading={isRegionsLoading}
                      />
                      {errors.region && <div className="admin-field-error">{errors.region}</div>}

                      <ScrollableDropdown
                        label="Province"
                        id="profile-province"
                        options={provinces}
                        value={selectedProvinceId}
                        onChange={(val) => {
                          handlePersonalInputChange("province", val);
                          setErrors((p) => ({ ...p, province: null }));
                        }}
                        placeholder="Select Province"
                        disabled={isProvincesLoading || !selectedRegionId}
                        loading={isProvincesLoading}
                      />
                      {errors.province && <div className="admin-field-error">{errors.province}</div>}
                    </div>

                    <div className="form-row">
                      <ScrollableDropdown
                        label="City/Municipality"
                        id="profile-city"
                        options={cities}
                        value={selectedCityId}
                        onChange={(val) => {
                          handlePersonalInputChange("city", val);
                          setErrors((p) => ({ ...p, city: null }));
                        }}
                        placeholder="Select City"
                        disabled={isCitiesLoading || !selectedProvinceId}
                        loading={isCitiesLoading}
                      />
                      {errors.city && <div className="admin-field-error">{errors.city}</div>}

                      <ScrollableDropdown
                        label="Barangay"
                        id="profile-barangay"
                        options={barangays}
                        value={selectedBarangayId}
                        onChange={(val) => {
                          handlePersonalInputChange("barangay", val);
                          setErrors((p) => ({ ...p, barangay: null }));
                        }}
                        placeholder="Select Barangay"
                        disabled={isBarangaysLoading || !selectedCityId}
                        loading={isBarangaysLoading}
                      />
                      {errors.barangay && <div className="admin-field-error">{errors.barangay}</div>}
                    </div>
                  </div>
                )}
              </div>
              
              {personalEdit && (
                <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                  <button
                    className="psp-btn-outline"
                    onClick={handlePersonalCancel}
                    disabled={isSaving}
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="button"
                    className="psp-btn-primary"
                    disabled={!personalValid || hasValidationErrors() || isSaving}
                    onClick={handlePersonalSave}
                  >
                    {isSaving ? t.updating : t.updateChanges}
                  </button>
                </div>
              )}
            </section>

            {/* Change Password */}
            <section className="psp-section" style={{ marginTop: '16px', padding: '16px' }}>
              <UserChangePassword 
                userData={personalData} 
                onPasswordOtpRequired={(data) => {
                  setPasswordChangeData(data);
                  setUserChangeType('password');
                  setShowUserChangeOtpModal(true);
                }}
              />
            </section>
          </div>

          {/* Right column */}
          <div className="psp-right">
            {/* Account Information box */}
            <div className="psp-box" style={{ padding: '16px' }}>
              <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                <h2 className="psp-section-title">{t.accountSettings}</h2>
                {!accountEdit && (
                  <button 
                    type="button" 
                    className="psp-btn-edit" 
                    onClick={() => setAccountEdit(true)}
                  >
                    Edit
                  </button>
                )}
              </div>
              
              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>Unique Identifier Code</label>
                <input type="text" value={personalData.uniqueIdentifierCode || "Not available"} readOnly />
              </div>
              
              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>Email</label>
                <input
                  type="email"
                  value={tempPersonalData.email}
                  maxLength={254}
                  readOnly={!accountEdit}
                  onChange={(e) =>
                    handleAccountInputChange("email", e.target.value)
                  }
                  onBlur={(e) => handleEmailBlur(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === " ") e.preventDefault();
                  }}
                  className={errors.email ? "input-error" : ""}
                />
                {errors.email && (
                  <div className="admin-field-error">
                    {errors.email}
                  </div>
                )}
              </div>
              
              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>Contact Number</label>
                {accountEdit ? (
                  <div className={`admin-contact-wrapper ${errors.contact ? "input-error" : ""}`} style={{ border: 'none', padding: 0 }}>
                    <span className="admin-country-code">+63</span>
                    <input
                      type="tel"
                      value={tempPersonalData.contact || ""}
                      maxLength={10}
                      onChange={(e) =>
                        handleAccountInputChange("contact", e.target.value)
                      }
                      onBlur={(e) => handleContactBlur(e.target.value)}
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    value={tempPersonalData.contact ? `+63 ${tempPersonalData.contact}` : ""}
                    readOnly
                  />
                )}
                {errors.contact && (
                  <div className="admin-field-error" style={{ textAlign: "left" }}>
                    {errors.contact}
                  </div>
                )}
              </div>
              
              {accountEdit && (
                <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                  <button
                    className="psp-btn-outline"
                    onClick={handleAccountCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="psp-btn-primary"
                    disabled={!accountValid || hasValidationErrors() || isSaving}
                    onClick={handleAccountSave}
                  >
                    {isSaving ? "Updating..." : "Save Changes"}
                  </button>
                </div>
              )}
            </div>

            {/* Assisted Account Section - if applicable */}
            {personalData.assistanceRequired === "yes" && (
              <div className="psp-box" style={{ marginTop: '16px', padding: '16px' }}>
                <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <h2 className="psp-section-title">Assisted Account</h2>
                    <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '400' }}>
                      Reason for assistance: {(() => {
                        const reasons = personalData.reasonForAssistance || [];
                        const labels = [];
                        if (reasons.includes('minor')) labels.push("Minor");
                        if (reasons.includes('pwd')) labels.push("PWD");
                        if (reasons.includes('nrnw')) labels.push("NRNW");
                        if (reasons.includes('senior')) labels.push("Senior/Elderly");
                        if (reasons.includes('others')) {
                          const text = personalData.reasonText || "";
                          labels.push(text ? `Other (${text})` : "Other");
                        }
                        return labels.join(", ");
                      })()}
                    </span>
                    {(personalData.disabilityText || personalData.reasonText) && (
                      <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '400', textAlign: 'left' }}>
                        {personalData.disabilityText && personalData.reasonText 
                          ? `${personalData.disabilityText}, ${personalData.reasonText}`
                          : personalData.disabilityText || personalData.reasonText}
                      </span>
                    )}
                  </div>
                  {!assistedEdit && (
                    <button 
                      type="button" 
                      className="psp-btn-edit" 
                      onClick={handleAssistedEdit}
                    >
                      Edit
                    </button>
                  )}
                </div>
                    
                <div style={{ 
                  marginBottom: '12px', 
                  padding: '10px 14px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0' 
                }}>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.875rem', 
                    color: '#475569', 
                    fontWeight: 500 
                  }}>
                    {personalData.reasonForAssistance?.includes('minor') 
                      ? "Guardian contact details for minor account" 
                      : "Accompanist contact details for assisted account"}
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>Full Name</label>
                    <input
                      type="text"
                      value={tempAssistedData.fullName}
                      readOnly={!assistedEdit}
                      onChange={(e) => handleAssistedInputChange("fullName", e.target.value)}
                      placeholder="Enter full name"
                    />
                  </div>

                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>Relationship</label>
                    {assistedEdit ? (
                      <>
                        <select
                          value={isAssistedOtherTyping ? "Other" : tempAssistedData.relationship}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "Other") {
                              setIsAssistedOtherTyping(true);
                              if (RELATIONSHIP_OPTIONS.includes(tempAssistedData.relationship)) {
                                setTempAssistedData(prev => ({ ...prev, relationship: "" }));
                              }
                            } else {
                              setIsAssistedOtherTyping(false);
                              setTempAssistedData(prev => ({ ...prev, relationship: value }));
                            }
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            borderRadius: '12px', 
                            border: '2px solid #e2e8f0',
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            cursor: assistedEdit ? 'pointer' : 'not-allowed'
                          }}
                        >
                          <option value="" disabled hidden>Select Relationship</option>
                          {RELATIONSHIP_OPTIONS.map((rel) => (
                            <option key={rel} value={rel}>{rel}</option>
                          ))}
                          <option value="Other">Other</option>
                        </select>
                        {isAssistedOtherTyping && (
                          <input
                            type="text"
                            placeholder="Specify Relationship"
                            value={tempAssistedData.relationship}
                            onChange={(e) => setTempAssistedData(prev => ({ ...prev, relationship: e.target.value }))}
                            style={{ marginTop: '10px' }}
                          />
                        )}
                      </>
                    ) : (
                      <input
                        type="text"
                        value={tempAssistedData.relationship}
                        readOnly
                        placeholder="Enter relationship"
                      />
                    )}
                  </div>

                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>Contact Number</label>
                    {assistedEdit ? (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        border: '2px solid #e2e8f0', 
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: '#fff'
                      }}>
                        <span style={{ 
                          padding: '12px 16px', 
                          background: '#f8fafc', 
                          color: '#475569', 
                          fontWeight: 600, 
                          fontSize: '14px', 
                          borderRight: '2px solid #e2e8f0' 
                        }}>
                          +63
                        </span>
                        <input
                          type="tel"
                          value={tempAssistedData.contact}
                          readOnly={!assistedEdit}
                          maxLength={10}
                          onChange={(e) => handleAssistedInputChange("contact", e.target.value)}
                          placeholder="9123456789"
                          style={{ 
                            border: 'none', 
                            borderRadius: 0, 
                            flex: 1, 
                            padding: '12px 16px',
                            width: '100%'
                          }}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={tempAssistedData.contact ? `+63 ${tempAssistedData.contact}` : ""}
                        readOnly
                      />
                    )}
                    {errors.assistedContact && (
                      <div className="admin-field-error" style={{ textAlign: "left", marginTop: '6px' }}>
                        {errors.assistedContact}
                      </div>
                    )}
                  </div>

                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>
                      {personalData.reasonForAssistance?.includes('minor') ? "Guardian Address" : "Accompanist Address"}
                    </label>
                    <input
                      type="text"
                      value={tempAssistedData.address}
                      readOnly={!assistedEdit}
                      onChange={(e) => handleAssistedInputChange("address", e.target.value)}
                      placeholder="Enter address"
                    />
                  </div>

                  
                </div>

                {assistedEdit && (
                  <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="psp-btn-outline"
                      onClick={handleAssistedCancel}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="psp-btn-primary"
                      disabled={isSaving || !!errors.assistedContact}
                      onClick={handleAssistedSave}
                    >
                      {isSaving ? "Updating..." : "Save Changes"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Access & Security box */}
            <div className="psp-box" style={{ marginTop: '16px', padding: '16px' }}>
              <h2 className="psp-section-title" style={{ marginBottom: '12px' }}>Access &amp; Security</h2>
              <div className="psp-toggle-row" style={{ marginBottom: '12px' }}>
                <div>
                  <div className="psp-toggle-label">Two-Factor Authentication</div>
                  <div className="psp-toggle-desc">Adds an extra layer of account security.</div>
                </div>
                <button
                  type="button"
                  className={`psp-toggle ${twoFactorEnabled ? "psp-toggle-on" : ""}`}
                  onClick={() => updateTwoFactor(!twoFactorEnabled)}
                  aria-label="Toggle two-factor authentication"
                >
                  <span className="psp-toggle-knob" />
                </button>
              </div>
              
              {/* Account Information */}
              <div className="psp-account-info" style={{ marginBottom: '12px' }}>
                <div className="psp-account-info-header">
                  <h3 className="psp-account-info-title">Account Information</h3>
                </div>
                <div className="psp-row-fields">
                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>Date Created</label>
                    <div className="psp-field-value" style={{ lineHeight: '1.5' }}>
                      <div>{parseFirebaseTimestamp(createdAt)?.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) || 'N/A'}</div>
                      <div style={{ fontSize: '0.85em', color: '#64748b' }}>{parseFirebaseTimestamp(createdAt)?.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase() || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>Last Login</label>
                    <div className="psp-field-value" style={{ lineHeight: '1.5' }}>
                      <div>{lastLogin ? parseFirebaseTimestamp(lastLogin)?.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : 'First Login'}</div>
                      <div style={{ fontSize: '0.85em', color: '#64748b' }}>{lastLogin ? parseFirebaseTimestamp(lastLogin)?.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase() : ''}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="psp-sessions">
                <div className="psp-sessions-header">
                  <div className="psp-sessions-label">Where You're Logged In</div>
                  <button
                    type="button"
                    className="psp-btn-revoke psp-btn-logout-all"
                    style={{ backgroundColor: 'transparent', borderColor: '#dc2626', color: '#dc2626' }}
                    onClick={handleLogoutAllClick}
                    disabled={logoutAllLoading}
                  >
                    {logoutAllLoading ? "Signing out…" : "Log Out All Sessions"}
                  </button>
                </div>

                {/* Logout-all confirmation */}
                {showLogoutAllConfirm && (
                  <div className="psp-session-confirm-overlay" role="dialog" aria-modal="true" aria-label="Confirm log out all sessions">
                    <div className="psp-session-confirm-box">
                      <p>This will sign you out of <strong>all devices</strong>. Continue?</p>
                      <div className="psp-session-confirm-actions" style={{ justifyContent: 'center' }}>
                        <button
                          type="button"
                          className="psp-btn-revoke"
                          style={{ borderColor: '#2563eb', color: '#2563eb', backgroundColor: 'transparent' }}
                          onClick={() => setShowLogoutAllConfirm(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="psp-btn-revoke"
                          style={{ borderColor: '#dc2626', color: '#dc2626', backgroundColor: 'transparent' }}
                          onClick={handleLogoutAllConfirm}
                        >
                          Yes, log out all
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {sessionsLoading ? (
                  <p className="psp-sessions-loading" style={{ fontSize: '0.85rem', color: '#64748b', padding: '8px 0' }}>
                    Loading sessions…
                  </p>
                ) : activeSessions.length === 0 ? (
                  <p className="psp-sessions-empty" style={{ fontSize: '0.85rem', color: '#64748b', padding: '8px 0' }}>
                    No active sessions found.
                  </p>
                ) : (
                  <ul className="psp-session-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {activeSessions.map((s) => {
                      const isCurrent = s.sessionId === currentSessionId;
                      const isRevoking = revokeLoadingId === s.sessionId;
                      const lastActive = s.lastActiveAt?.toDate?.() ?? null;
                      const lastActiveLabel = lastActive
                        ? formatSessionTime(lastActive)
                        : "Unknown";
                      return (
                        <li key={s.sessionId} className={`psp-session-item${isCurrent ? " psp-session-item--current" : ""}`}>
                          <div className="psp-session-info">
                            <span className="psp-session-device">
                              {s.deviceName || `${s.browser || "Unknown"} on ${s.operatingSystem || "Unknown"}`}
                              {isCurrent && (
                                <span className="psp-session-badge-current" style={{
                                  marginLeft: '8px',
                                  fontSize: '0.72rem',
                                  background: '#dcfce7',
                                  color: '#15803d',
                                  borderRadius: '4px',
                                  padding: '1px 6px',
                                  fontWeight: 600,
                                }}>
                                  Current session
                                </span>
                              )}
                            </span>
                            <span className="psp-session-location" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                              {s.deviceType || "Desktop"} · Last active: {lastActiveLabel}
                            </span>
                          </div>
                          {!isCurrent && (
                            <button
                              type="button"
                              className="psp-btn-revoke"
                              onClick={() => handleRevokeSession(s.sessionId)}
                              disabled={isRevoking || logoutAllLoading}
                              aria-label={`Revoke session on ${s.deviceName}`}
                            >
                              {isRevoking ? "Revoking…" : "Revoke"}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Health Information Grid */}
      <div className="psp-main" style={{ marginTop: '0.5rem' }}>
        <div className="psp-grid">
          {/* Left Column - Health Info */}
          <div className="psp-left">
            {/* Health & Lifestyle */}
            <section className="psp-section" style={{ padding: '16px' }}>
              <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                <h2 className="psp-section-title">Health & Lifestyle</h2>
                {!healthEditing && (
                  <button 
                    type="button" 
                    className="psp-btn-edit" 
                    onClick={handleHealthEdit}
                  >
                    Edit
                  </button>
                )}
              </div>
              
              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>Health / Medical History</label>
                {(() => {
                  const hasSelection = medicalHistory.length > 0;
                  if (!healthEditing && !hasSelection) {
                    return <p className="no-information-text">No Information</p>;
                  }
                  return (
                    <>
                      <div className="hh-chip-group">
                        {(healthEditing ? CONDITIONS : CONDITIONS.filter(item => medicalHistory.includes(item))).map((item) => (
                          <button
                            key={item}
                            className={`hh-chip ${medicalHistory.includes(item) ? "hh-chip--selected" : ""} ${!healthEditing && !medicalHistory.includes(item) ? "hh-chip--disabled" : ""}`}
                            disabled={!healthEditing && !medicalHistory.includes(item)}
                            onClick={() => toggleItem(item, medicalHistory, setMedicalHistory, setMedicalOther)}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                      {medicalHistory.includes("Others (Please Specify)") && (
                        <div className="hh-char-counter-wrapper">
                          <textarea 
                            type="text" 
                            className="hh-other-input" 
                            placeholder="Please specify" 
                            disabled={!healthEditing} 
                            maxLength={150}
                            rows={3}
                            value={medicalOther} 
                            onChange={(e) => {
                              setMedicalOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                              if (healthFieldErrors.medicalOther) {
                                setHealthFieldErrors(prev => ({ ...prev, medicalOther: null }));
                              }
                            }} 
                          />
                          {healthEditing && <span className="hh-char-counter">{medicalOther.length}/150</span>}
                          {healthEditing && healthFieldErrors.medicalOther && !medicalOther.trim() && (
                            <span className="hh-field-error">{healthFieldErrors.medicalOther}</span>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>Medication Allergies</label>
                {(() => {
                  const hasSelection = allergies.length > 0;
                  if (!healthEditing && !hasSelection) {
                    return <p className="no-information-text">No Information</p>;
                  }
                  return (
                    <>
                      <div className="hh-chip-group">
                        {(healthEditing ? ALLERGIES.filter(item => userSex !== "male" || item !== "Hormonal Medications (Pills, Patches, Injections)") : ALLERGIES.filter(item => allergies.includes(item) && (userSex !== "male" || item !== "Hormonal Medications (Pills, Patches, Injections)"))).map((item) => (
                          <button
                            key={item}
                            className={`hh-chip ${allergies.includes(item) ? "hh-chip--selected" : ""} ${!healthEditing && !allergies.includes(item) ? "hh-chip--disabled" : ""}`}
                            disabled={!healthEditing && !allergies.includes(item)}
                            onClick={() =>
                              toggleItem(
                                item,
                                allergies,
                                setAllergies,
                                item === "Others (Please Specify)"
                                  ? setAllergyOther
                                  : item === "Hormonal Medications (Pills, Patches, Injections)"
                                    ? setHormonalMedicationText
                                    : undefined
                              )
                            }
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                      {userSex !== "male" && allergies.includes("Hormonal Medications (Pills, Patches, Injections)") && (
                        <div className="hh-char-counter-wrapper">
                          <textarea 
                            type="text" 
                            className="hh-other-input" 
                            placeholder="Please specify hormonal medications" 
                            disabled={!healthEditing} 
                            maxLength={150}
                            rows={3}
                            value={hormonalMedicationText} 
                            onChange={(e) => {
                              setHormonalMedicationText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                              if (healthFieldErrors.hormonalMedicationText) {
                                setHealthFieldErrors(prev => ({ ...prev, hormonalMedicationText: null }));
                              }
                            }} 
                          />
                          {healthEditing && <span className="hh-char-counter">{hormonalMedicationText.length}/150</span>}
                          {healthEditing && healthFieldErrors.hormonalMedicationText && !hormonalMedicationText.trim() && (
                            <span className="hh-field-error">{healthFieldErrors.hormonalMedicationText}</span>
                          )}
                        </div>
                      )}

                      {allergies.includes("Others (Please Specify)") && (
                        <div className="hh-char-counter-wrapper">
                          <textarea 
                            type="text" 
                            className="hh-other-input" 
                            placeholder="Please specify" 
                            disabled={!healthEditing} 
                            maxLength={150}
                            rows={3}
                            value={allergyOther} 
                            onChange={(e) => {
                              setAllergyOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                              if (healthFieldErrors.allergyOther) {
                                setHealthFieldErrors(prev => ({ ...prev, allergyOther: null }));
                              }
                            }} 
                          />
                          {healthEditing && <span className="hh-char-counter">{allergyOther.length}/150</span>}
                          {healthEditing && healthFieldErrors.allergyOther && !allergyOther.trim() && (
                            <span className="hh-field-error">{healthFieldErrors.allergyOther}</span>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>Lifestyle Indicators</label>
                {(() => {
                  const hasSelection = alcoholUse || smoking;
                  if (!healthEditing && !hasSelection) {
                    return <p className="no-information-text">No Information</p>;
                  }
                  return (
                    <div className="hh-lifestyle-rows">
                      <div className="hh-lifestyle-row-inline">
                        <img src={alcoholIcon} alt="Alcohol Icon" className="hh-lifestyle-icon" />
                        <span className="hh-lifestyle-label">Alcohol Use</span>
                        <div className="hh-radio-group">
                          {(healthEditing ? ["Regularly", "Occasionally", "Never"] : (alcoholUse ? [alcoholUse] : [])).map((opt) => (
                            <label key={opt} className={`hh-radio-pill ${alcoholUse === opt ? 'hh-radio-pill--checked' : ''}`}>
                              <input type="radio" name="alcohol" disabled={!healthEditing} checked={alcoholUse === opt} onChange={() => setAlcoholUse(opt)} />
                              <div className="hh-custom-radio"></div>
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="hh-lifestyle-row-inline">
                        <img src={cigaretteIcon} alt="Cigarette Icon" className="hh-lifestyle-icon" />
                        <span className="hh-lifestyle-label">Smoking</span>
                        <div className="hh-radio-group">
                          {(healthEditing ? ["Regularly", "Occasionally", "Never"] : (smoking ? [smoking] : [])).map((opt) => (
                            <label key={opt} className={`hh-radio-pill ${smoking === opt ? 'hh-radio-pill--checked' : ''}`}>
                              <input type="radio" name="smoking" disabled={!healthEditing} checked={smoking === opt} onChange={() => setSmoking(opt)} />
                              <div className="hh-custom-radio"></div>
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {healthEditing && (
                <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                  <button
                    className="psp-btn-outline"
                    onClick={handleHealthCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="psp-btn-primary"
                    onClick={handleHealthSave}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </section>

            {/* Medication History */}
            <section className="psp-section" style={{ marginTop: '16px', padding: '16px' }}>
              <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                <h2 className="psp-section-title">Medication History</h2>
                {!additionalEditing && (
                  <button 
                    type="button" 
                    className="psp-btn-edit" 
                    onClick={handleAdditionalEdit}
                  >
                    Edit
                  </button>
                )}
              </div>
              
              {userSex !== "male" && (
                <div className="psp-field" style={{ marginBottom: '10px' }}>
                  <label style={{ marginBottom: '6px' }}>Currently on Hormonal Contraception</label>
                  {(() => {
                    const hasSelection = additionalHistory.length > 0;
                    if (!additionalEditing && !hasSelection) {
                      return <p className="no-information-text">No Information</p>;
                    }
                    return (
                      <>
                        <div className="hh-chip-group">
                          {(additionalEditing ? MEDICATION_HISTORY : MEDICATION_HISTORY.filter(item => additionalHistory.includes(item))).map((item) => (
                            <button
                              key={item}
                              className={`hh-chip ${additionalHistory.includes(item) ? "hh-chip--selected" : ""} ${!additionalEditing && !additionalHistory.includes(item) ? "hh-chip--disabled" : ""}`}
                              disabled={!additionalEditing && !additionalHistory.includes(item)}
                              onClick={() => toggleItem(item, additionalHistory, setAdditionalHistory, setContraceptionOtherText)}
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                        {additionalHistory.includes("Others (Please Specify)") && (
                          <div className="hh-char-counter-wrapper">
                            <textarea
                              type="text"
                              className="hh-other-input"
                              placeholder="Please specify"
                              disabled={!additionalEditing}
                              maxLength={150}
                              rows={3}
                              value={contraceptionOtherText}
                              onChange={(e) => {
                                setContraceptionOtherText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                                if (additionalFieldErrors.contraceptionOtherText) {
                                  setAdditionalFieldErrors(prev => ({ ...prev, contraceptionOtherText: null }));
                                }
                              }}
                            />
                            {additionalEditing && <span className="hh-char-counter">{contraceptionOtherText.length}/150</span>}
                            {additionalEditing && additionalFieldErrors.contraceptionOtherText && !contraceptionOtherText.trim() && (
                              <span className="hh-field-error">{additionalFieldErrors.contraceptionOtherText}</span>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>Past Surgeries</label>
                {(() => {
                  const PAST_SURGERIES_LOCAL = [
                    "Abdominal Section",
                    "Pelvic Surgery",
                    ...(userSex === "male" ? ["Prostate Surgery", "Testicular Surgery"] : []),
                    ...(userSex === "female" ? ["Cesarean Section", "Hysterectomy"] : []),
                    "Others (Please Specify)"
                  ];
                  const hasSelection = leftBox.length > 0;
                  if (!additionalEditing && !hasSelection) {
                    return <p className="no-information-text">No Information</p>;
                  }
                  return (
                    <>
                      <div className="hh-chip-group">
                        {(additionalEditing ? PAST_SURGERIES_LOCAL : PAST_SURGERIES_LOCAL.filter(item => leftBox.includes(item))).map((item) => (
                          <button
                            key={item}
                            className={`hh-chip ${leftBox.includes(item) ? "hh-chip--selected" : ""} ${!additionalEditing && !leftBox.includes(item) ? "hh-chip--disabled" : ""}`}
                            disabled={!additionalEditing && !leftBox.includes(item)}
                            onClick={() => toggleItem(item, leftBox, setLeftBox, setLeftBoxOther)}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                      {leftBox.includes("Others (Please Specify)") && (
                        additionalEditing ? (
                          <div className="hh-char-counter-wrapper">
                            <textarea
                              type="text"
                              className="hh-other-input"
                              placeholder="Please specify"
                              disabled={!additionalEditing}
                              maxLength={150}
                              rows={3}
                              value={leftBoxOther}
                              onChange={(e) => {
                                setLeftBoxOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                                if (additionalFieldErrors.leftBoxOther) {
                                  setAdditionalFieldErrors(prev => ({ ...prev, leftBoxOther: null }));
                                }
                              }}
                            />
                            {additionalEditing && <span className="hh-char-counter">{leftBoxOther.length}/150</span>}
                            {additionalEditing && additionalFieldErrors.leftBoxOther && !leftBoxOther.trim() && (
                              <span className="hh-field-error">{additionalFieldErrors.leftBoxOther}</span>
                            )}
                          </div>
                        ) : (
                          <div className="hh-other-input">
                            {leftBoxOther || "-"}
                          </div>
                        )
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>Current Medication</label>
                {(() => {
                  const hasSelection = rightBoxText;
                  if (!additionalEditing && !hasSelection) {
                    return <p className="no-information-text">No Information</p>;
                  }
                  return (
                    additionalEditing ? (
                      <div className="hh-char-counter-wrapper">
                        <textarea
                          className="hh-other-input"
                          rows={5}
                          disabled={!additionalEditing}
                          maxLength={150}
                          value={rightBoxText}
                          onChange={(e) => setRightBoxText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''))}
                        />
                        {additionalEditing && <span className="hh-char-counter">{rightBoxText.length}/150</span>}
                      </div>
                    ) : (
                      <div className="hh-other-input">
                        {rightBoxText || "-"}
                      </div>
                    )
                  );
                })()}
              </div>

              {additionalEditing && (
                <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                  <button
                    className="psp-btn-outline"
                    onClick={handleAdditionalCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="psp-btn-primary"
                    onClick={handleAdditionalSave}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </section>
          </div>

          {/* Right Column - Health Info */}
          <div className="psp-right">
            {/* Sexual Health History */}
            <section className="psp-section" style={{ padding: '16px' }}>
              <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                <h2 className="psp-section-title">Sexual Health History</h2>
                {!hivEditing && (
                  <button 
                    type="button" 
                    className="psp-btn-edit" 
                    onClick={handleHivEdit}
                  >
                    Edit
                  </button>
                )}
              </div>
              
              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>History of Sexually Transmitted Infections (STIs)</label>
                {(() => {
                  const hasSelection = hivSelection.length > 0;
                  if (!hivEditing && !hasSelection) {
                    return <p className="no-information-text">No Information</p>;
                  }
                  return (
                    <>
                      <div className="hh-chip-group">
                        {(hivEditing ? INFECTIONS_OPTIONS : INFECTIONS_OPTIONS.filter(opt => hivSelection.includes(opt))).map(opt => (
                          <button
                            key={opt}
                            className={`hh-chip ${hivSelection.includes(opt) ? "hh-chip--selected" : ""} ${!hivEditing ? "hh-chip--disabled" : ""}`}
                            disabled={!hivEditing}
                            onClick={() => {
                              if (hivSelection.includes(opt)) {
                                setHivSelection(hivSelection.filter(i => i !== opt));
                                if (opt === "Others (Please Specify)") setStiOther("");
                              } else {
                                setHivSelection([...hivSelection, opt]);
                              }
                            }}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                      {hivSelection.includes("Others (Please Specify)") && (
                        <div className="hh-char-counter-wrapper">
                          <textarea 
                            type="text" 
                            className="hh-other-input" 
                            placeholder="Please specify" 
                            disabled={!hivEditing} 
                            maxLength={150}
                            rows={3}
                            value={stiOther} 
                            onChange={(e) => {
                              setStiOther(e.target.value.replace(/[^A-Za-z\s'-]/g, ''));
                              if (stiFieldErrors.stiOther) {
                                setStiFieldErrors(prev => ({ ...prev, stiOther: null }));
                              }
                            }} 
                          />
                          {hivEditing && <span className="hh-char-counter">{stiOther.length}/150</span>}
                          {hivEditing && stiFieldErrors.stiOther && !stiOther.trim() && (
                            <span className="hh-field-error">{stiFieldErrors.stiOther}</span>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>HIV Status</label>
                {(() => {
                  const hasSelection = hivLeftBox;
                  if (!hivEditing && !hasSelection) {
                    return <p className="no-information-text">No Information</p>;
                  }
                  return (
                    <div className="radio-list">
                      {(hivEditing ? HIV_OPTIONS : (hivLeftBox ? [hivLeftBox] : [])).map((opt) => (
                        <div key={opt} className="hiv-row-container">
                          <label className="radio-box">
                            <input
                              type="radio"
                              name="hivStatus"
                              disabled={!hivEditing}
                              checked={hivLeftBox === opt}
                              onChange={() => {
                                setHivLeftBox(opt);
                                setErrors((prev) => ({ ...prev, hivStatus: "" }));
                                if (opt === "Unknown") {
                                  setLastTestedDate("");
                                  setErrors((prev) => ({ ...prev, lastTestedDate: "" }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                            <span className="option-text">{opt}</span>
                          </label>
                          {opt !== "Unknown" && hivLeftBox === opt && (
                            <div className="date-input-container">
                              <input
                                type="date"
                                className={`styled-date-input ${errors.lastTestedDate ? "input-error" : ""}`}
                                disabled={!hivEditing}
                                value={lastTestedDate || ""}
                                min={personalData.dob}
                                max={new Date().toISOString().split('T')[0]}
                                onChange={(e) => {
                                  setLastTestedDate(e.target.value.slice(0, 10));
                                  setErrors((prev) => ({ ...prev, lastTestedDate: "" }));
                                }}
                              />
                              {errors.lastTestedDate && (
                                <div className="admin-field-error" style={{ fontSize: '11px' }}>
                                  {errors.lastTestedDate}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div className="psp-field" style={{ marginBottom: '10px' }}>
                <label style={{ marginBottom: '6px' }}>History of Genital Infections / Other Conditions</label>
                {(() => {
                  const hasSelection = hivRightBoxText;
                  if (!hivEditing && !hasSelection) {
                    return <p className="no-information-text">No Information</p>;
                  }
                  return (
                    <div className="hh-char-counter-wrapper">
                      <textarea 
                        className="hh-other-input" 
                        rows={5} 
                        disabled={!hivEditing} 
                        maxLength={150}
                        value={hivRightBoxText} 
                        onChange={e => setHivRightBoxText(e.target.value.replace(/[^A-Za-z\s'-]/g, ''))} 
                      />
                      {hivEditing && <span className="hh-char-counter">{hivRightBoxText.length}/150</span>}
                    </div>
                  );
                })()}
              </div>

              {hivEditing && (
                <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                  <button
                    className="psp-btn-outline"
                    onClick={handleHivCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="psp-btn-primary"
                    onClick={handleHivSave}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </section>

            {/* Emergency Contact */}
            <section className="psp-section" style={{ marginTop: '16px', padding: '16px' }}>
              <div className="psp-section-header" style={{ marginBottom: '12px' }}>
                <h2 className="psp-section-title">Emergency Contact</h2>
                {!clinicalEditing && (
                  <button 
                    type="button" 
                    className="psp-btn-edit" 
                    onClick={handleClinicalEdit}
                  >
                    Edit
                  </button>
                )}
              </div>
              
              <div style={{ marginTop: '10px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>Name</label>
                    <input
                      type="text"
                      placeholder="Full Name"
                      disabled={!clinicalEditing}
                      maxLength={50}
                      value={emergencyName}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(/[^a-zA-Z'\- ]/g, '');
                        setEmergencyName(sanitized.slice(0, 50));
                        validateField("name", sanitized.slice(0, 50));
                      }}
                      className={errors.name ? "input-error" : ""}
                    />
                    {errors.name && (
                      <div className="admin-field-error">{errors.name}</div>
                    )}
                  </div>

                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>Relationship</label>
                    {clinicalEditing ? (
                      <>
                        <select
                          disabled={!clinicalEditing}
                          value={isOtherTyping ? "Other" : emergencyRelationship}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value === "Other") {
                              setIsOtherTyping(true);
                              if (RELATIONSHIP_OPTIONS.includes(emergencyRelationship)) {
                                setEmergencyRelationship("Other");
                              }
                            } else {
                              setIsOtherTyping(false);
                              setEmergencyRelationship(value);
                              setEmergencyRelationshipOther("");
                              validateField("relationship", value);
                            }
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            borderRadius: '12px', 
                            border: '2px solid #e2e8f0',
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            cursor: clinicalEditing ? 'pointer' : 'not-allowed'
                          }}
                        >
                          <option value="" disabled hidden>
                            Select Relationship
                          </option>
                          {RELATIONSHIP_OPTIONS.map((rel) => (
                            <option key={rel} value={rel}>
                              {rel}
                            </option>
                          ))}
                          <option value="Other">Other</option>
                        </select>

                        {isOtherTyping && (
                          <input
                            type="text"
                            placeholder="Specify Relationship"
                            disabled={!clinicalEditing}
                            value={emergencyRelationshipOther}
                            onChange={(e) => {
                              const sanitized = e.target.value.replace(/[^a-zA-Z]/g, '');
                              setEmergencyRelationshipOther(sanitized.slice(0, 20));
                              validateField("relationship", sanitized.slice(0, 20));
                            }}
                            maxLength={20}
                            style={{ marginTop: '8px' }}
                          />
                        )}
                      </>
                    ) : (
                      <input
                        type="text"
                        value={isOtherTyping ? emergencyRelationshipOther : emergencyRelationship}
                        readOnly
                        placeholder="Enter relationship"
                      />
                    )}
                    {errors.relationship && (
                      <div className="admin-field-error">{errors.relationship}</div>
                    )}
                  </div>

                  <div className="psp-field" style={{ marginBottom: '0' }}>
                    <label style={{ marginBottom: '6px' }}>Contact Number</label>
                    {clinicalEditing ? (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        border: `2px solid ${errors.contact ? '#dc2626' : '#e2e8f0'}`, 
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: '#fff'
                      }}>
                        <span style={{ 
                          padding: '12px 16px', 
                          background: '#f8fafc', 
                          color: '#475569', 
                          fontWeight: 600, 
                          fontSize: '14px', 
                          borderRight: '2px solid #e2e8f0' 
                        }}>
                          +63
                        </span>
                        <input
                          type="text"
                          placeholder="912345678"
                          disabled={!clinicalEditing}
                          value={emergencyContact}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            setEmergencyContact(digits);
                            validateField("contact", digits);
                          }}
                          maxLength={10}
                          style={{ 
                            border: 'none', 
                            borderRadius: 0, 
                            flex: 1, 
                            padding: '12px 16px',
                            width: '100%'
                          }}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={emergencyContact ? `+63 ${emergencyContact}` : ""}
                        readOnly
                      />
                    )}
                    {errors.contact && (
                      <div className="admin-field-error" style={{ textAlign: "left" }}>
                        {errors.contact}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {clinicalEditing && (
                <div className="psp-edit-actions" style={{ marginTop: '12px' }}>
                  <button
                    className="psp-btn-outline"
                    onClick={handleClinicalCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="psp-btn-primary"
                    onClick={handleClinicalSave}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
      )

      </div>

      <UserChangeOtpModal
        isOpen={showUserChangeOtpModal}
        onClose={() => {
          setShowUserChangeOtpModal(false);
          setUserChangeData(null);
          setUserChangeType(null);
          setPasswordChangeData(null);
        }}
        changeType={userChangeType}
        changeData={userChangeType === 'password' ? passwordChangeData : userChangeData}
        onSuccess={async (result) => {
          // Capture the pending change data before clearing it
          const completedChangeData = userChangeData;

          setShowUserChangeOtpModal(false);
          setUserChangeData(null);
          setUserChangeType(null);
          setPasswordChangeData(null);
          
          if (result.type === 'email' && result.requireReauth) {
            navigate('/login');
            return;
          }
          
          if (result.type === 'password' && result.requireReauth) {
            // Logout and redirect to login after password change.
            // Delete the current session document directly before signing out.
            sessionStorage.setItem('logoutInProgress', 'true');
            const currentUser = auth.currentUser;
            if (currentUser) {
              await sessionService.deleteCurrentSession(currentUser.uid).catch(() => {});
            }
            sessionService.clearCurrentSession();
            try {
              await auth.signOut();
            } catch (_) {}
            localStorage.clear();
            setTimeout(() => sessionStorage.removeItem('logoutInProgress'), 1000);
            navigate('/login');
            return;
          }
          
          // Immediately update displayed state with the new values so the
          // UI reflects the change without waiting for the Firestore round-trip
          if (completedChangeData?.updatedData) {
            const updated = completedChangeData.updatedData;
            const immediateUpdate = (prev) => ({
              ...prev,
              ...(updated.email !== undefined && { email: updated.email }),
              ...(updated.contact !== undefined && { contact: updated.contact }),
            });
            setPersonalData(immediateUpdate);
            setTempPersonalData(immediateUpdate);
            setOriginalPersonalData(immediateUpdate);
          }

          // Refresh user data after successful change
          const loggedInUser = getStoredUser();
          if (loggedInUser?.uid) {
            loadUserData();
          }

          showBanner("Profile updated successfully!", "success");

          // Reset edit mode
          setPersonalEdit(false);
          setAccountEdit(false);
          setAssistedEdit(false);
        }}
        userEmail={personalData?.email}
        userPhone={personalData?.phone || personalData?.contact}
        userFirstName={personalData?.firstName}
      />


        </>
      )}
    </div>
  );
}


export default AccountPanel;
